import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PermissionGuard from '../components/Auth/PermissionGuard';
import toast from 'react-hot-toast';
import { 
  PaintBrushIcon,
  SwatchIcon,
  DocumentTextIcon,
  PhotoIcon,
  SparklesIcon,
  EyeIcon,
  CheckCircleIcon,
  CreditCardIcon,
  MegaphoneIcon,
  DocumentDuplicateIcon,
  CubeIcon,
  TruckIcon
} from '@heroicons/react/24/outline';

const AdminSettings = () => {
  const { user, getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [savingShipping, setSavingShipping] = useState(false);
  const [savingPayments, setSavingPayments] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');
  const [uploadingImages, setUploadingImages] = useState({
    banner: false,
    left: false,
    right: false,
    invoice_logo: false,
    logo: false,
    special_offer: false,
    products_hero: false
  });

  const normalizeUploadUrl = useCallback((url) => {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('/api/uploads/')) return trimmed;
    if (trimmed.startsWith('/uploads/')) return `/api${trimmed}`;
    return trimmed;
  }, []);

  const normalizeUploadList = useCallback((value) => {
    return (value || '')
      .split(',')
      .map((url) => normalizeUploadUrl(url))
      .filter((url) => url)
      .join(', ');
  }, [normalizeUploadUrl]);
  
  // Design configuration state
  const [designConfig, setDesignConfig] = useState({
    // Branding
    site_name: 'Viverra',
    site_tagline: 'Your trusted shopping destination',
    logo_text: 'V',
    logo_image: '', // Uploaded logo image URL
    about_text: 'Viverra brings you a curated collection of elegant fashion, designer bags, and jewelry accessories.',
    
    // Colors
    primary_color: '#4F46E5',
    secondary_color: '#7C3AED',
    accent_color: '#EC4899',
    background_color: '#F9FAFB',
    text_color: '#111827',
    header_bg: '#FFFFFF',
    footer_bg: '#1F2937',
    footer_text: '#F9FAFB',
    
    // Hero Section
    hero_title: 'Luxury E-Commerce',
    hero_subtitle: 'Discover premium products, exclusive collections, and unmatched quality at your fingertips',
    hero_button_text: 'Explore Collection',
    
    // Features
    feature_1_title: 'Lightning Fast Shipping',
    feature_1_description: 'Express delivery worldwide with free shipping on orders over ₹5000',
    feature_2_title: 'Bank-Level Security',
    feature_2_description: 'Advanced encryption and secure payment processing for peace of mind',
    feature_3_title: 'Premium Support',
    feature_3_description: 'Dedicated customer service team available 24/7 for your needs',
    
    // Footer
    footer_about: 'Elegant fashion, designer bags, and jewelry - curated for everyday and special occasions.',
    footer_copyright: '',
    contact_email: '',
    contact_phone: '',
    social_facebook: '',
    social_twitter: '',
    social_instagram: '',
    social_linkedin: '',
    
    // Announcement Banner
    announcement_enabled: false,
    announcement_text: 'Welcome to our store!',
    announcement_text_color: '#ffffff',
    announcement_bg_color: '#000000',
    announcement_speed: 'medium', // slow, medium, fast
    announcement_scope: 'all', // all, selected
    
    // Invoice Settings
    invoice_company_name: 'Viverra',
    invoice_company_address: '',
    invoice_company_city: '',
    invoice_company_state: '',
    invoice_company_zip: '',
    invoice_company_country: '',
    invoice_company_phone: '',
    invoice_company_email: '',
    invoice_company_website: '',
    invoice_logo: '',
    invoice_footer_text: 'Thank you for your business!',
    invoice_terms: 'All sales are final. Returns accepted within 30 days.',
    invoice_tax_id: '',
    invoice_gst_number: '',
    
    // Product Image Settings
    product_image_min: 1,
    product_image_max: 10,

    // Home: Special Offer Banner
    special_offer_banner_image: '',
    special_offer_label: 'SPECIAL OFFER',
    special_offer_percent_text: '10%',
    special_offer_off_text: 'OFF',
    special_offer_button_text: 'Shop Now',
    special_offer_button_link: '/products',
    special_offer_enabled: 'true',
    special_offer_autoplay: 'true',
    special_offer_autoplay_speed: '4000',
    special_offer_scroll_direction: 'rtl',
    special_offer_swipe_enabled: 'true',

    // Products Page Hero
    products_hero_enabled: 'true',
    products_hero_use_image: 'false',
    products_hero_background_image: '',
    products_hero_overlay_enabled: 'true',
    products_hero_autoplay: 'true',
    products_hero_autoplay_speed: '4000',
    products_hero_scroll_direction: 'rtl',
    products_hero_swipe_enabled: 'true',
    products_hero_top_label: 'Collection',
    products_hero_title: 'Our Collection',
    products_hero_subtitle: 'Discover our curated selection of elegant fashion, designer bags, and luxury accessories',

    // Home Hero Carousels (H1/H2/H3)
    hero_banner_autoplay: 'true',
    hero_banner_autoplay_speed: '4000',
    hero_banner_scroll_direction: 'rtl',
    hero_banner_swipe_enabled: 'true',
    hero_left_autoplay: 'true',
    hero_left_autoplay_speed: '4000',
    hero_left_scroll_direction: 'rtl',
    hero_left_swipe_enabled: 'true',
    hero_right_autoplay: 'true',
    hero_right_autoplay_speed: '4000',
    hero_right_scroll_direction: 'rtl',
    hero_right_swipe_enabled: 'true',
    hero_banner_enabled: 'true',
    hero_left_enabled: 'true',
    hero_right_enabled: 'true',
    
    // Hero Banner Size Settings
    hero_left_min_height: 300,
    hero_right_min_height: 300
  });

  const [deliveryConfig, setDeliveryConfig] = useState({
    mode: 'allowlist-with-deny',
    allowlistText: '',
    denylistText: '',
    codMode: 'inherit',
    codAllowlistText: '',
    codDenylistText: ''
  });

  const [shippingConfig, setShippingConfig] = useState({
    enabled: true,
    flatCharge: 500,
    freeOver: 5000
  });

  const [deliveryTest, setDeliveryTest] = useState({
    pincode: '',
    loading: false,
    result: null,
    error: ''
  });

  const [paymentConfig, setPaymentConfig] = useState({
    upiEnabled: false,
    upiVpa: '',
    upiPayeeName: '',
    upiMerchantCode: '',
    upiWebsiteUrl: '',
    upiNotePrefix: 'Order',
    paytmEnabled: false,
    paytmMid: '',
    paytmMerchantKey: '',
    paytmEnvironment: 'STAGING',
    paytmWebsiteName: 'WEBSTAGING',
    paytmCallbackUrl: '',
    paytmChannelId: 'WEB',

    // Payment Links (automatic confirmation)
    paymentLinksDefaultProvider: 'auto',
    razorpayEnabled: false,
    razorpayKeyId: '',
    razorpayKeySecret: '',
    razorpayWebhookSecret: '',
    razorpayCallbackUrl: '',
    cashfreeEnabled: false,
    cashfreeClientId: '',
    cashfreeClientSecret: '',
    cashfreeWebhookSecret: '',
    cashfreeEnvironment: 'SANDBOX',
    cashfreeApiVersion: '2023-08-01',
    cashfreeReturnUrl: ''
  });

  const [notificationConfig, setNotificationConfig] = useState({
    fromName: 'Viverra Fancy',
    fromEmail: '',
    subject: 'Reset your password',
    introText: 'We received a request to reset your password.',
    footerText: 'If you did not request this, you can ignore this email.',
    resetLinkBaseUrl: '',
    smsEnabled: false,
    smsTemplate: 'Reset your Viverra account password using this link: {{reset_link}}'
  });
  const [savingNotifications, setSavingNotifications] = useState(false);

  const [tieredPromoConfig, setTieredPromoConfig] = useState({
    enabled: false,
    code: '',
    startDate: '',
    endDate: '',
    minOrderAmount: 0,
    maxDiscount: '',
    tier1Percent: 10,
    tier2Percent: 5,
    tier3Percent: 2,
    allowedCategoryIdsText: '',
    allowedProductIdsText: ''
  });

  const [savingPromo, setSavingPromo] = useState(false);

  const createEmptyPromoRule = () => ({
    id: `promo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    code: '',
    name: '',
    enabled: true,
    type: 'bundle_buy_x_get_y',
    startDate: '',
    endDate: '',
    minOrderAmount: 0,
    repeatable: true,
    stackable: false,
    maxDiscount: '',
    bagProductIdsText: '',
    bottleProductIdsText: '',
    tiffinProductIdsText: '',
    rewardBottleProductIdsText: '',
    rewardTiffinProductIdsText: ''
  });

  const [promoRulesConfig, setPromoRulesConfig] = useState({
    version: 2,
    promos: []
  });
  const [savingPromoRules, setSavingPromoRules] = useState(false);

  const parseIdListText = (text) => {
    const items = String(text || '')
    .split(/[\s,]+/)
      .map(x => x.trim())
      .filter(Boolean);

    const nums = items
      .map(x => parseInt(x, 10))
      .filter(n => Number.isFinite(n) && n > 0);

    return Array.from(new Set(nums));
  };

  const idListToText = (list) => (Array.isArray(list) ? list.join('\n') : '');

  const mapPromoRuleToEditor = (promo = {}) => ({
    id: promo.id || `promo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    code: promo.code || '',
    name: promo.name || '',
    enabled: promo.enabled !== undefined ? Boolean(promo.enabled) : true,
    type: promo.type || 'bundle_buy_x_get_y',
    startDate: promo.startDate || '',
    endDate: promo.endDate || '',
    minOrderAmount: Number(promo.minOrderAmount || 0),
    repeatable: promo.repeatable !== undefined ? Boolean(promo.repeatable) : true,
    stackable: promo.stackable !== undefined ? Boolean(promo.stackable) : false,
    maxDiscount: promo.maxDiscount ?? '',
    bagProductIdsText: idListToText((promo.buyGroups || []).find(group => group.key === 'bag')?.productIds),
    bottleProductIdsText: idListToText((promo.buyGroups || []).find(group => group.key === 'bottle')?.productIds),
    tiffinProductIdsText: idListToText((promo.buyGroups || []).find(group => group.key === 'tiffin')?.productIds),
    rewardBottleProductIdsText: idListToText((promo.rewardGroups || []).find(group => group.key === 'bottle')?.productIds),
    rewardTiffinProductIdsText: idListToText((promo.rewardGroups || []).find(group => group.key === 'tiffin')?.productIds)
  });

  const promoRuleEditorToPayload = (promo) => ({
    id: String(promo.id || '').trim(),
    code: String(promo.code || '').trim().toUpperCase(),
    name: String(promo.name || '').trim(),
    enabled: Boolean(promo.enabled),
    type: String(promo.type || 'bundle_buy_x_get_y').trim(),
    startDate: String(promo.startDate || '').trim() || null,
    endDate: String(promo.endDate || '').trim() || null,
    minOrderAmount: Number(promo.minOrderAmount || 0) || 0,
    repeatable: Boolean(promo.repeatable),
    stackable: Boolean(promo.stackable),
    maxDiscount: String(promo.maxDiscount || '').trim() ? Number(promo.maxDiscount) : null,
    buyGroups: [
      { key: 'bag', label: 'Bag', minQty: 1, productIds: parseIdListText(promo.bagProductIdsText) },
      { key: 'bottle', label: 'Bottle', minQty: 1, productIds: parseIdListText(promo.bottleProductIdsText) },
      { key: 'tiffin', label: 'Tiffin', minQty: 1, productIds: parseIdListText(promo.tiffinProductIdsText) }
    ],
    rewardGroups: [
      { key: 'bottle', label: 'Bottle', freeQty: 1, productIds: parseIdListText(promo.rewardBottleProductIdsText) },
      { key: 'tiffin', label: 'Tiffin', freeQty: 1, productIds: parseIdListText(promo.rewardTiffinProductIdsText) }
    ]
  });

  const fetchPromoRulesConfig = useCallback(async () => {
    try {
      const token = getToken(true);
      if (!token) return;

      const response = await fetch('/api/settings/admin', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (!response.ok || !data?.success) return;

      const value = data?.data?.settings?.promo_rules?.value;
      const promos = Array.isArray(value?.promos) ? value.promos.map(mapPromoRuleToEditor) : [];
      setPromoRulesConfig({
        version: Number(value?.version || 2) || 2,
        promos
      });
    } catch (error) {
      console.warn('⚠️ [AdminSettings] Promo rules config load failed:', error?.message);
    }
  }, [getToken]);

  const handleSavePromoRules = async () => {
    setSavingPromoRules(true);
    try {
      const token = getToken(true);
      if (!token) throw new Error('Missing admin token');

      const promos = promoRulesConfig.promos.map(promoRuleEditorToPayload);
      const enabledCodes = new Set();

      for (const promo of promos) {
        if (!promo.code) {
          throw new Error('Each promo rule needs a promo code');
        }

        if (enabledCodes.has(promo.code)) {
          throw new Error(`Duplicate promo code: ${promo.code}`);
        }
        enabledCodes.add(promo.code);

        if (promo.type === 'bundle_buy_x_get_y') {
          const hasBag = promo.buyGroups.some(group => group.key === 'bag' && group.productIds.length > 0);
          const hasBottle = promo.buyGroups.some(group => group.key === 'bottle' && group.productIds.length > 0);
          const hasTiffin = promo.buyGroups.some(group => group.key === 'tiffin' && group.productIds.length > 0);
          const hasRewardBottle = promo.rewardGroups.some(group => group.key === 'bottle' && group.productIds.length > 0);
          const hasRewardTiffin = promo.rewardGroups.some(group => group.key === 'tiffin' && group.productIds.length > 0);

          if (!(hasBag && hasBottle && hasTiffin && hasRewardBottle && hasRewardTiffin)) {
            throw new Error(`Promo ${promo.code} must define eligible bag, bottle, and tiffin products plus reward bottle and tiffin products`);
          }
        }
      }

      const payload = {
        version: 2,
        promos
      };

      const response = await fetch('/api/settings/setting/promo_rules', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          value: payload,
          type: 'json',
          description: 'Versioned promo rules configuration (supports multiple promo codes and bundle rules)'
        })
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || data?.error || `HTTP ${response.status}`);
      }

      toast.success('Promo rules saved!', { icon: '✅', duration: 2500 });
      await fetchPromoRulesConfig();
    } catch (error) {
      console.error('Error saving promo rules:', error);
      toast.error(error.message || 'Failed to save promo rules');
    } finally {
      setSavingPromoRules(false);
    }
  };

  const addPromoRule = () => {
    setPromoRulesConfig(prev => ({
      ...prev,
      promos: [...prev.promos, createEmptyPromoRule()]
    }));
  };

  const updatePromoRule = (promoId, field, value) => {
    setPromoRulesConfig(prev => ({
      ...prev,
      promos: prev.promos.map((promo) => promo.id === promoId ? { ...promo, [field]: value } : promo)
    }));
  };

  const removePromoRule = (promoId) => {
    setPromoRulesConfig(prev => ({
      ...prev,
      promos: prev.promos.filter((promo) => promo.id !== promoId)
    }));
  };

  const fetchTieredPromoConfig = useCallback(async () => {
    try {
      const token = getToken(true); // Dashboard token
      if (!token) return;

      const response = await fetch('/api/settings/admin', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (!response.ok || !data?.success) return;

      const setting = data?.data?.settings?.tiered_promo;
      const value = setting?.value;
      if (value && typeof value === 'object') {
        const tiers = value.tiers || {};
        setTieredPromoConfig(prev => ({
          ...prev,
          enabled: Boolean(value.enabled),
          code: value.code || '',
          startDate: value.startDate || '',
          endDate: value.endDate || '',
          minOrderAmount: Number(value.minOrderAmount || 0),
          maxDiscount: value.maxDiscount ?? '',
          tier1Percent: Number(tiers['1'] ?? tiers[1] ?? prev.tier1Percent),
          tier2Percent: Number(tiers['2'] ?? tiers[2] ?? prev.tier2Percent),
          tier3Percent: Number(tiers['3'] ?? tiers[3] ?? prev.tier3Percent),
          allowedCategoryIdsText: idListToText(value.allowedCategoryIds),
          allowedProductIdsText: idListToText(value.allowedProductIds)
        }));
      }
    } catch (error) {
      console.warn('⚠️ [AdminSettings] Tiered promo config load failed:', error?.message);
    }
  }, [getToken]);

  const handleSaveTieredPromo = async () => {
    setSavingPromo(true);
    try {
      const token = getToken(true); // Dashboard token
      if (!token) throw new Error('Missing admin token');

      const code = String(tieredPromoConfig.code || '').trim();
      if (tieredPromoConfig.enabled && !code) {
        toast.error('Promo code is required');
        return;
      }

      const payload = {
        enabled: Boolean(tieredPromoConfig.enabled),
        code,
        startDate: String(tieredPromoConfig.startDate || '').trim() || null,
        endDate: String(tieredPromoConfig.endDate || '').trim() || null,
        minOrderAmount: Number(tieredPromoConfig.minOrderAmount || 0) || 0,
        maxDiscount: String(tieredPromoConfig.maxDiscount || '').trim() ? Number(tieredPromoConfig.maxDiscount) : null,
        tiers: {
          1: Number(tieredPromoConfig.tier1Percent || 0) || 0,
          2: Number(tieredPromoConfig.tier2Percent || 0) || 0,
          3: Number(tieredPromoConfig.tier3Percent || 0) || 0
        },
        allowedCategoryIds: parseIdListText(tieredPromoConfig.allowedCategoryIdsText),
        allowedProductIds: parseIdListText(tieredPromoConfig.allowedProductIdsText)
      };

      const response = await fetch('/api/settings/setting/tiered_promo', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          value: payload,
          type: 'json',
          description: 'Tiered promo code settings (1st/2nd/3rd delivered purchase discounts)'
        })
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || data?.error || `HTTP ${response.status}`);
      }

      toast.success('Promo settings saved!', { icon: '✅', duration: 2500 });
      await fetchTieredPromoConfig();
    } catch (error) {
      console.error('Error saving promo settings:', error);
      toast.error(error.message || 'Failed to save promo settings');
    } finally {
      setSavingPromo(false);
    }
  };

  const fetchDesignConfig = useCallback(async () => {
    try {
      const response = await fetch('/api/settings/design');
      const data = await response.json();
      if (data.success && data.data.design) {
        setDesignConfig(prev => ({ ...prev, ...data.data.design }));
      }
    } catch (error) {
      console.error('Error fetching design config:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const parsePincodeList = (text) => {
    const items = String(text || '')
      .split(/[,\n\r\t\s]+/)
      .map(x => x.trim())
      .filter(Boolean);

    const unique = Array.from(new Set(items));
    return unique.filter(pin => /^[1-9][0-9]{5}$/.test(pin));
  };

  const listToText = (list) => (Array.isArray(list) ? list.join('\n') : '');

  const fetchDeliveryConfig = useCallback(async () => {
    try {
      const response = await fetch('/api/settings');
      const data = await response.json();
      const setting = data?.data?.settings?.delivery_serviceability;
      const value = setting?.value;

      if (value && typeof value === 'object') {
        setDeliveryConfig(prev => ({
          ...prev,
          mode: value.mode || prev.mode,
          allowlistText: listToText(value.allowlist),
          denylistText: listToText(value.denylist),
          codMode: value.cod?.mode || prev.codMode,
          codAllowlistText: listToText(value.cod?.allowlist),
          codDenylistText: listToText(value.cod?.denylist)
        }));
      }
    } catch (error) {
      console.error('Error fetching delivery config:', error);
    }
  }, []);

  const fetchShippingConfig = useCallback(async () => {
    try {
      const response = await fetch('/api/settings');
      const data = await response.json();
      const setting = data?.data?.settings?.shipping_config;
      const value = setting?.value;

      if (value && typeof value === 'object') {
        setShippingConfig(prev => ({
          ...prev,
          enabled: value.enabled !== undefined ? Boolean(value.enabled) : prev.enabled,
          flatCharge: Number.isFinite(Number(value.flatCharge)) ? Number(value.flatCharge) : prev.flatCharge,
          freeOver: Number.isFinite(Number(value.freeOver)) ? Number(value.freeOver) : prev.freeOver
        }));
      }
    } catch (error) {
      console.error('Error fetching shipping config:', error);
    }
  }, []);

  const handleSaveShipping = async () => {
    setSavingShipping(true);
    try {
      const token = getToken(true); // Dashboard token
      if (!token) {
        toast.error('Not authenticated');
        return;
      }

      const payload = {
        enabled: Boolean(shippingConfig.enabled),
        flatCharge: Math.max(0, Number(shippingConfig.flatCharge) || 0),
        freeOver: Math.max(0, Number(shippingConfig.freeOver) || 0)
      };

      const response = await fetch('/api/settings/setting/shipping_config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          value: payload,
          type: 'json',
          description: 'Shipping charge configuration'
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP ${response.status}`);
      }

      if (data.success) {
        toast.success('Shipping settings saved!', { icon: '✅', duration: 2500 });
        await fetchShippingConfig();
      } else {
        toast.error(data.message || 'Failed to save shipping settings');
      }
    } catch (error) {
      console.error('Error saving shipping settings:', error);
      toast.error(error.message || 'Failed to save shipping settings');
    } finally {
      setSavingShipping(false);
    }
  };

  const fetchPaymentConfig = useCallback(async () => {
    try {
      const response = await fetch('/api/settings');
      const data = await response.json();
      const setting = data?.data?.settings?.payment_upi;
      const value = setting?.value;

      if (value && typeof value === 'object') {
        setPaymentConfig(prev => ({
          ...prev,
          upiEnabled: Boolean(value.enabled),
          upiVpa: value.vpa || '',
          upiPayeeName: value.payeeName || '',
          upiMerchantCode: value.merchantCode || '',
          upiWebsiteUrl: value.websiteUrl || '',
          upiNotePrefix: value.notePrefix || prev.upiNotePrefix
        }));
      }

      // Also load non-secret Payment Links flags from public settings.
      // (Secrets are masked on /api/settings; the admin-only endpoint below loads them.)
      const publicLinksSetting = data?.data?.settings?.payment_links;
      const publicLinksValue = publicLinksSetting?.value;
      if (publicLinksValue && typeof publicLinksValue === 'object') {
        setPaymentConfig(prev => ({
          ...prev,
          paymentLinksDefaultProvider: String(publicLinksValue.defaultProvider || prev.paymentLinksDefaultProvider).toLowerCase(),
          razorpayEnabled: Boolean(publicLinksValue?.razorpay?.enabled),
          razorpayKeyId: publicLinksValue?.razorpay?.keyId || prev.razorpayKeyId,
          cashfreeEnabled: Boolean(publicLinksValue?.cashfree?.enabled),
          cashfreeClientId: publicLinksValue?.cashfree?.clientId || prev.cashfreeClientId,
          cashfreeEnvironment: String(publicLinksValue?.cashfree?.environment || prev.cashfreeEnvironment).toUpperCase(),
          cashfreeApiVersion: publicLinksValue?.cashfree?.apiVersion || prev.cashfreeApiVersion,
          cashfreeReturnUrl: publicLinksValue?.cashfree?.returnUrl || prev.cashfreeReturnUrl
        }));
      }

      // Load Paytm PG config from admin-only settings endpoint (includes merchantKey)
      try {
        const token = getToken(true);
        if (token) {
          const adminRes = await fetch('/api/settings/admin', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const adminJson = await adminRes.json();
          const paytmSetting = adminJson?.data?.settings?.payment_paytm;
          const paytmValue = paytmSetting?.value;

          if (paytmValue && typeof paytmValue === 'object') {
            setPaymentConfig(prev => ({
              ...prev,
              paytmEnabled: Boolean(paytmValue.enabled),
              paytmMid: paytmValue.mid || '',
              paytmMerchantKey: paytmValue.merchantKey || '',
              paytmEnvironment: String(paytmValue.environment || prev.paytmEnvironment).toUpperCase(),
              paytmWebsiteName: paytmValue.websiteName || prev.paytmWebsiteName,
              paytmCallbackUrl: paytmValue.callbackUrl || '',
              paytmChannelId: paytmValue.channelId || prev.paytmChannelId
            }));
          }

          const linksSetting = adminJson?.data?.settings?.payment_links;
          const linksValue = linksSetting?.value;
          if (linksValue && typeof linksValue === 'object') {
            setPaymentConfig(prev => ({
              ...prev,
              paymentLinksDefaultProvider: String(linksValue.defaultProvider || prev.paymentLinksDefaultProvider).toLowerCase(),
              razorpayEnabled: Boolean(linksValue?.razorpay?.enabled),
              razorpayKeyId: linksValue?.razorpay?.keyId || '',
              razorpayKeySecret: linksValue?.razorpay?.keySecret || '',
              razorpayWebhookSecret: linksValue?.razorpay?.webhookSecret || '',
              razorpayCallbackUrl: linksValue?.razorpay?.callbackUrl || '',
              cashfreeEnabled: Boolean(linksValue?.cashfree?.enabled),
              cashfreeClientId: linksValue?.cashfree?.clientId || '',
              cashfreeClientSecret: linksValue?.cashfree?.clientSecret || '',
              cashfreeWebhookSecret: linksValue?.cashfree?.webhookSecret || '',
              cashfreeEnvironment: String(linksValue?.cashfree?.environment || prev.cashfreeEnvironment).toUpperCase(),
              cashfreeApiVersion: linksValue?.cashfree?.apiVersion || prev.cashfreeApiVersion,
              cashfreeReturnUrl: linksValue?.cashfree?.returnUrl || ''
            }));
          }
        }
      } catch (e) {
        console.warn('⚠️ [AdminSettings] Paytm config load failed:', e?.message);
      }
    } catch (error) {
      console.error('Error fetching payment config:', error);
    }
  }, [getToken]);

  const fetchNotificationConfig = useCallback(async () => {
    try {
      const token = getToken(true);
      if (!token) {
        return;
      }

      const response = await fetch('/api/settings/admin', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        return;
      }

      const setting = data?.data?.settings?.password_reset_notifications;
      const value = setting?.value;
      if (value && typeof value === 'object') {
        setNotificationConfig((prev) => ({
          ...prev,
          ...value,
          smsEnabled: Boolean(value.smsEnabled)
        }));
      }
    } catch (error) {
      console.warn('⚠️ [AdminSettings] Notification config load failed:', error?.message);
    }
  }, [getToken]);

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      const token = getToken(true);
      if (!token) {
        toast.error('Not authenticated');
        return;
      }

      const payload = {
        fromName: String(notificationConfig.fromName || '').trim(),
        fromEmail: String(notificationConfig.fromEmail || '').trim(),
        subject: String(notificationConfig.subject || '').trim() || 'Reset your password',
        introText: String(notificationConfig.introText || '').trim() || 'We received a request to reset your password.',
        footerText: String(notificationConfig.footerText || '').trim() || 'If you did not request this, you can ignore this email.',
        resetLinkBaseUrl: String(notificationConfig.resetLinkBaseUrl || '').trim(),
        smsEnabled: Boolean(notificationConfig.smsEnabled),
        smsTemplate: String(notificationConfig.smsTemplate || '').trim()
      };

      const response = await fetch('/api/settings/setting/password_reset_notifications', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          value: payload,
          type: 'json',
          description: 'Customer password reset notification settings (email + SMS template metadata)'
        })
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || data?.error || `HTTP ${response.status}`);
      }

      toast.success('Notification settings saved!', { icon: '✅', duration: 2500 });
      await fetchNotificationConfig();
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast.error(error.message || 'Failed to save notification settings');
    } finally {
      setSavingNotifications(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchDesignConfig();
      fetchDeliveryConfig();
      fetchShippingConfig();
      fetchPaymentConfig();
      fetchNotificationConfig();
      fetchPromoRulesConfig();
      fetchTieredPromoConfig();
    }
  }, [fetchDeliveryConfig, fetchDesignConfig, fetchNotificationConfig, fetchPaymentConfig, fetchPromoRulesConfig, fetchShippingConfig, fetchTieredPromoConfig, user]);

  const isValidUpiVpa = (vpa) => {
    const s = String(vpa || '').trim();
    // Basic VPA format: name@bank
    return /^[A-Za-z0-9._-]{2,256}@[A-Za-z]{2,64}$/.test(s);
  };

  const handleSavePayments = async () => {
    setSavingPayments(true);
    try {
      const token = getToken(true); // Dashboard token

      if (paymentConfig.upiEnabled) {
        if (!isValidUpiVpa(paymentConfig.upiVpa)) {
          toast.error('Please enter a valid UPI ID (VPA), e.g. yourname@okaxis');
          return;
        }
        if (!String(paymentConfig.upiPayeeName || '').trim()) {
          toast.error('Please enter Payee Name');
          return;
        }
      }

      const payload = {
        enabled: Boolean(paymentConfig.upiEnabled),
        vpa: String(paymentConfig.upiVpa || '').trim(),
        payeeName: String(paymentConfig.upiPayeeName || '').trim(),
        merchantCode: String(paymentConfig.upiMerchantCode || '').trim() || null,
        websiteUrl: String(paymentConfig.upiWebsiteUrl || '').trim() || null,
        notePrefix: String(paymentConfig.upiNotePrefix || 'Order').trim() || 'Order'
      };

      const response = await fetch('/api/settings/setting/payment_upi', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          value: payload,
          type: 'json',
          description: 'UPI payment settings (Google Pay / PhonePe / Paytm intent)'
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP ${response.status}`);
      }

      if (data.success) {
        // Save Payment Links providers config (admin-only)
        const paymentLinksPayload = {
          defaultProvider: String(paymentConfig.paymentLinksDefaultProvider || 'auto').trim().toLowerCase(),
          razorpay: {
            enabled: Boolean(paymentConfig.razorpayEnabled),
            keyId: String(paymentConfig.razorpayKeyId || '').trim(),
            keySecret: String(paymentConfig.razorpayKeySecret || '').trim(),
            webhookSecret: String(paymentConfig.razorpayWebhookSecret || '').trim(),
            callbackUrl: String(paymentConfig.razorpayCallbackUrl || '').trim() || null
          },
          cashfree: {
            enabled: Boolean(paymentConfig.cashfreeEnabled),
            clientId: String(paymentConfig.cashfreeClientId || '').trim(),
            clientSecret: String(paymentConfig.cashfreeClientSecret || '').trim(),
            webhookSecret: String(paymentConfig.cashfreeWebhookSecret || '').trim(),
            environment: String(paymentConfig.cashfreeEnvironment || 'SANDBOX').trim().toUpperCase(),
            apiVersion: String(paymentConfig.cashfreeApiVersion || '2023-08-01').trim(),
            returnUrl: String(paymentConfig.cashfreeReturnUrl || '').trim() || null
          }
        };

        if (paymentLinksPayload.razorpay.enabled) {
          if (!paymentLinksPayload.razorpay.keyId) {
            toast.error('Razorpay Key ID is required');
            return;
          }
          if (!paymentLinksPayload.razorpay.keySecret) {
            toast.error('Razorpay Key Secret is required');
            return;
          }
          if (!paymentLinksPayload.razorpay.webhookSecret) {
            toast.error('Razorpay Webhook Secret is required');
            return;
          }
        }

        if (paymentLinksPayload.cashfree.enabled) {
          if (!paymentLinksPayload.cashfree.clientId) {
            toast.error('Cashfree Client ID is required');
            return;
          }
          if (!paymentLinksPayload.cashfree.clientSecret) {
            toast.error('Cashfree Client Secret is required');
            return;
          }
          if (!paymentLinksPayload.cashfree.webhookSecret) {
            toast.error('Cashfree Webhook Secret is required');
            return;
          }
        }

        const linksRes = await fetch('/api/settings/setting/payment_links', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            value: paymentLinksPayload,
            type: 'json',
            description: 'Payment link providers (Razorpay/Cashfree) configuration'
          })
        });
        const linksJson = await linksRes.json();
        if (!linksRes.ok || !linksJson.success) {
          throw new Error(linksJson.message || linksJson.error || `HTTP ${linksRes.status}`);
        }

        // Save Paytm settings too
        const paytmPayload = {
          enabled: Boolean(paymentConfig.paytmEnabled),
          mid: String(paymentConfig.paytmMid || '').trim(),
          merchantKey: String(paymentConfig.paytmMerchantKey || '').trim(),
          environment: String(paymentConfig.paytmEnvironment || 'STAGING').toUpperCase(),
          websiteName: String(paymentConfig.paytmWebsiteName || '').trim(),
          callbackUrl: String(paymentConfig.paytmCallbackUrl || '').trim() || null,
          channelId: String(paymentConfig.paytmChannelId || 'WEB').trim() || 'WEB'
        };

        if (paytmPayload.enabled) {
          if (!paytmPayload.mid) {
            toast.error('Paytm MID is required');
            return;
          }
          if (!paytmPayload.merchantKey) {
            toast.error('Paytm Merchant Key is required');
            return;
          }
        }

        const paytmRes = await fetch('/api/settings/setting/payment_paytm', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            value: paytmPayload,
            type: 'json',
            description: 'Paytm Payment Gateway configuration'
          })
        });

        const paytmJson = await paytmRes.json();
        if (!paytmRes.ok || !paytmJson.success) {
          throw new Error(paytmJson.message || paytmJson.error || `HTTP ${paytmRes.status}`);
        }

        toast.success('Payment settings saved!', { icon: '✅', duration: 2500 });
        await fetchPaymentConfig();
      } else {
        toast.error(data.message || 'Failed to save payment settings');
      }
    } catch (error) {
      console.error('Error saving payment settings:', error);
      toast.error(error.message || 'Failed to save payment settings');
    } finally {
      setSavingPayments(false);
    }
  };

  const handleSaveDelivery = async () => {
    setSavingDelivery(true);
    try {
      const token = getToken(true); // Dashboard token

      const payload = {
        mode: deliveryConfig.mode,
        allowlist: parsePincodeList(deliveryConfig.allowlistText),
        denylist: parsePincodeList(deliveryConfig.denylistText),
        cod: {
          mode: deliveryConfig.codMode,
          allowlist: parsePincodeList(deliveryConfig.codAllowlistText),
          denylist: parsePincodeList(deliveryConfig.codDenylistText)
        }
      };

      const response = await fetch('/api/settings/setting/delivery_serviceability', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          value: payload,
          type: 'json',
          description: 'Delivery serviceability rules (India pincodes)'
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP ${response.status}`);
      }

      if (data.success) {
        toast.success('Delivery rules saved successfully!', { icon: '✅', duration: 2500 });
        await fetchDeliveryConfig();
      } else {
        toast.error(data.message || 'Failed to save delivery rules');
      }
    } catch (error) {
      console.error('Error saving delivery rules:', error);
      toast.error(error.message || 'Failed to save delivery rules');
    } finally {
      setSavingDelivery(false);
    }
  };

  const handleTestPincode = async () => {
    const pin = String(deliveryTest.pincode || '').trim();
    if (!/^[1-9][0-9]{5}$/.test(pin)) {
      setDeliveryTest(prev => ({ ...prev, error: 'Enter a valid 6-digit pincode', result: null }));
      return;
    }

    setDeliveryTest(prev => ({ ...prev, loading: true, error: '', result: null }));
    try {
      const res = await fetch(`/api/geo/serviceability/${encodeURIComponent(pin)}`);
      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.message || 'Failed to check serviceability');
      }
      setDeliveryTest(prev => ({ ...prev, loading: false, result: json.data, error: '' }));
    } catch (e) {
      setDeliveryTest(prev => ({ ...prev, loading: false, error: e.message || 'Failed', result: null }));
    }
  };

  const handleChange = (key, value) => {
    setDesignConfig(prev => ({ ...prev, [key]: value }));
  };

  // Handle image upload for hero section
  const handleHeroImageUpload = async (files, imageType) => {
    if (!files || files.length === 0) return;

    setUploadingImages(prev => ({ ...prev, [imageType]: true }));

    try {
      const token = getToken(true); // Dashboard token
      const uploadedImages = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch('/api/upload/image', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const data = await response.json();
        if (data.success && data.data?.image_url) {
          uploadedImages.push(data.data.image_url);
        }
      }

      if (uploadedImages.length > 0) {
        if (imageType === 'banner') {
          const currentImages = designConfig.hero_banner_images || '';
          const imagesArray = currentImages ? currentImages.split(',').map((url) => normalizeUploadUrl(url)).filter((url) => url) : [];
          handleChange('hero_banner_images', [...imagesArray, ...uploadedImages.map((url) => normalizeUploadUrl(url))].join(', '));
        } else if (imageType === 'left') {
          const currentImages = designConfig.hero_left_image || '';
          const imagesArray = currentImages ? currentImages.split(',').map(url => url.trim()).filter(url => url) : [];
          handleChange('hero_left_image', [...imagesArray, ...uploadedImages].join(', '));
        } else if (imageType === 'right') {
          const currentImages = designConfig.hero_right_image || '';
          const imagesArray = currentImages ? currentImages.split(',').map(url => url.trim()).filter(url => url) : [];
          handleChange('hero_right_image', [...imagesArray, ...uploadedImages].join(', '));
        }
        toast.success(`${uploadedImages.length} image(s) uploaded successfully!`);
      } else {
        toast.error('Failed to upload image(s)');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImages(prev => ({ ...prev, [imageType]: false }));
    }
  };

  // Handle website logo upload
  const handleLogoUpload = async (file) => {
    if (!file) return;

    setUploadingImages(prev => ({ ...prev, logo: true }));

    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = getToken(true); // Dashboard token
      const response = await fetch('/api/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        const imageUrl = data.data.image_url;
        handleChange('logo_image', imageUrl);
        toast.success('Logo uploaded successfully!');
      } else {
        toast.error(data.message || 'Failed to upload logo');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingImages(prev => ({ ...prev, logo: false }));
    }
  };

  // Handle invoice logo upload
  const handleInvoiceLogoUpload = async (file) => {
    if (!file) return;

    setUploadingImages(prev => ({ ...prev, invoice_logo: true }));

    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = getToken(true); // Dashboard token
      const response = await fetch('/api/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        const imageUrl = data.data.image_url;
        handleChange('invoice_logo', imageUrl);
        toast.success('Logo uploaded successfully!');
      } else {
        toast.error(data.message || 'Failed to upload logo');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingImages(prev => ({ ...prev, invoice_logo: false }));
    }
  };

  // Handle Special Offer banner image upload (Home page)
  const handleSpecialOfferBannerUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploadingImages(prev => ({ ...prev, special_offer: true }));

    try {
      const token = getToken(true); // Dashboard token
      const uploadedImages = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch('/api/upload/image', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const data = await response.json();
        if (data.success && data.data?.image_url) {
          uploadedImages.push(data.data.image_url);
        }
      }

      if (uploadedImages.length > 0) {
        const currentImages = (designConfig.special_offer_banner_image || '')
          .split(',')
          .map((url) => url.trim())
          .filter((url) => url);
        handleChange('special_offer_banner_image', [...currentImages, ...uploadedImages].join(', '));
        toast.success(`${uploadedImages.length} special offer image(s) uploaded successfully!`);
      } else {
        toast.error('Failed to upload banner image(s)');
      }
    } catch (error) {
      console.error('Error uploading special offer banner:', error);
      toast.error('Failed to upload banner image');
    } finally {
      setUploadingImages(prev => ({ ...prev, special_offer: false }));
    }
  };

  const handleProductsHeroBannerUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploadingImages(prev => ({ ...prev, products_hero: true }));

    try {
      const token = getToken(true);
      const uploadedImages = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch('/api/upload/image', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const data = await response.json();
        if (data.success && data.data?.image_url) {
          uploadedImages.push(data.data.image_url);
        }
      }

      if (uploadedImages.length > 0) {
        const currentImages = (designConfig.products_hero_background_image || '')
          .split(',')
          .map((url) => url.trim())
          .filter((url) => url);
        handleChange('products_hero_background_image', [...currentImages, ...uploadedImages].join(', '));
        toast.success(`${uploadedImages.length} products hero image(s) uploaded successfully!`);
      } else {
        toast.error('Failed to upload products hero image(s)');
      }
    } catch (error) {
      console.error('Error uploading products hero image:', error);
      toast.error('Failed to upload products hero image');
    } finally {
      setUploadingImages(prev => ({ ...prev, products_hero: false }));
    }
  };

  // Remove image from list
  const removeImage = (imageType, indexToRemove) => {
    let currentImages = '';
    if (imageType === 'banner') {
      currentImages = designConfig.hero_banner_images || '';
      const imagesArray = currentImages.split(',').map((url) => normalizeUploadUrl(url)).filter((url) => url);
      imagesArray.splice(indexToRemove, 1);
      handleChange('hero_banner_images', imagesArray.join(', '));
    } else if (imageType === 'left') {
      currentImages = designConfig.hero_left_image || '';
      const imagesArray = currentImages.split(',').map(url => url.trim()).filter(url => url);
      imagesArray.splice(indexToRemove, 1);
      handleChange('hero_left_image', imagesArray.join(', '));
    } else if (imageType === 'right') {
      currentImages = designConfig.hero_right_image || '';
      const imagesArray = currentImages.split(',').map(url => url.trim()).filter(url => url);
      imagesArray.splice(indexToRemove, 1);
      handleChange('hero_right_image', imagesArray.join(', '));
    } else if (imageType === 'special_offer') {
      currentImages = designConfig.special_offer_banner_image || '';
      const imagesArray = currentImages.split(',').map(url => url.trim()).filter(url => url);
      imagesArray.splice(indexToRemove, 1);
      handleChange('special_offer_banner_image', imagesArray.join(', '));
    } else if (imageType === 'products_hero') {
      currentImages = designConfig.products_hero_background_image || '';
      const imagesArray = currentImages.split(',').map(url => url.trim()).filter(url => url);
      imagesArray.splice(indexToRemove, 1);
      handleChange('products_hero_background_image', imagesArray.join(', '));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = getToken(true); // Dashboard token
      if (!token) {
        toast.error('Session expired. Please login again.');
        return;
      }
      const response = await fetch('/api/settings/design', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...designConfig,
          hero_banner_images: normalizeUploadList(designConfig.hero_banner_images),
          hero_left_image: normalizeUploadList(designConfig.hero_left_image),
          hero_right_image: normalizeUploadList(designConfig.hero_right_image),
          special_offer_banner_image: normalizeUploadList(designConfig.special_offer_banner_image),
          products_hero_background_image: normalizeUploadList(designConfig.products_hero_background_image),
          logo_image: normalizeUploadUrl(designConfig.logo_image),
          invoice_logo: normalizeUploadUrl(designConfig.invoice_logo)
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP ${response.status}`);
      }
      
      if (data.success) {
        toast.success(data.message || 'Design configuration saved successfully!', {
          icon: '✅',
          duration: 3000,
        });
        // Apply CSS variables
        applyDesignToPage();
      } else {
        const errorMsg = data.message || data.error || 'Failed to save design configuration';
        console.error('Save failed:', data);
        toast.error(errorMsg);
        if (data.errors && data.errors.length > 0) {
          console.error('Failed settings:', data.errors);
        }
      }
    } catch (error) {
      console.error('Error saving design config:', error);
      const errorMessage = error.message || 'Failed to save design configuration. Please check console for details.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const applyDesignToPage = useCallback(() => {
    // Apply CSS variables to root
    const root = document.documentElement;
    root.style.setProperty('--primary-color', designConfig.primary_color);
    root.style.setProperty('--secondary-color', designConfig.secondary_color);
    root.style.setProperty('--accent-color', designConfig.accent_color);
    root.style.setProperty('--background-color', designConfig.background_color);
    root.style.setProperty('--text-color', designConfig.text_color);
    root.style.setProperty('--header-bg', designConfig.header_bg);
    root.style.setProperty('--footer-bg', designConfig.footer_bg);
    root.style.setProperty('--footer-text', designConfig.footer_text);
  }, [designConfig]);

  // Apply design on config change
  useEffect(() => {
    applyDesignToPage();
  }, [applyDesignToPage]);

  const tabs = [
    { id: 'branding', name: 'Branding', icon: SparklesIcon },
    { id: 'colors', name: 'Colors', icon: SwatchIcon },
    { id: 'hero', name: 'Hero Section', icon: PhotoIcon },
    { id: 'features', name: 'Features', icon: SparklesIcon },
    { id: 'footer', name: 'Footer', icon: DocumentTextIcon },
    { id: 'announcement', name: 'Announcement Banner', icon: MegaphoneIcon },
    { id: 'promotions', name: 'Promotions', icon: MegaphoneIcon },
    { id: 'invoice', name: 'Invoice Design', icon: DocumentDuplicateIcon },
    { id: 'product', name: 'Product Settings', icon: CubeIcon },
    { id: 'payments', name: 'Payments', icon: CreditCardIcon },
    { id: 'notifications', name: 'Notifications', icon: DocumentTextIcon },
    { id: 'delivery', name: 'Delivery', icon: TruckIcon },
    { id: 'preview', name: 'Preview', icon: EyeIcon }
  ];

  const getFooterSaveHandler = () => {
    if (activeTab === 'notifications') return handleSaveNotifications;
    if (activeTab === 'payments') return handleSavePayments;
    if (activeTab === 'delivery') return handleSaveDelivery;
    if (activeTab === 'promotions') return handleSavePromoRules;
    return handleSave;
  };

  const getFooterSaveLabel = () => {
    if (activeTab === 'notifications') return 'Save Notification Settings';
    if (activeTab === 'payments') return 'Save Payment Settings';
    if (activeTab === 'delivery') return 'Save Delivery Rules';
    if (activeTab === 'promotions') return 'Save Promo Rules';
    return 'Save All Changes';
  };

  const isFooterSaving =
    saving ||
    savingNotifications ||
    savingPayments ||
    savingDelivery ||
    savingPromo ||
    savingPromoRules;

  const activeTabConfig = tabs.find((tab) => tab.id === activeTab) || tabs[0];

  return (
    <PermissionGuard permission="settings.view">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 sm:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="flex items-start gap-3 text-3xl font-bold text-gray-900 sm:items-center sm:text-4xl">
            <PaintBrushIcon className="h-8 w-8 shrink-0 text-indigo-600 sm:h-10 sm:w-10" />
            <span>Website Design Configuration</span>
          </h1>
          <p className="text-gray-600">Customize your entire website design, colors, branding, and content</p>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="sm:hidden">
            <label htmlFor="settings-tab-select" className="mb-2 block text-sm font-semibold text-gray-700">
              Settings section
            </label>
            <select
              id="settings-tab-select"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {tab.name}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-500">
              Current section: <span className="font-semibold text-gray-700">{activeTabConfig.name}</span>
            </p>
          </div>

          <div className="border-b border-gray-200">
            <nav className="-mb-px hidden gap-4 overflow-x-auto pb-2 pr-4 sm:flex sm:gap-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors
                      ${activeTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>
          </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-4 shadow-xl sm:p-6 lg:p-8">
            {/* Branding Tab */}
            {activeTab === 'branding' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Branding & Identity</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Site Name *
                    </label>
                    <input
                      type="text"
                      value={designConfig.site_name}
                      onChange={(e) => handleChange('site_name', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Your Store Name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Site Tagline
                    </label>
                    <input
                      type="text"
                      value={designConfig.site_tagline}
                      onChange={(e) => handleChange('site_tagline', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Your tagline here"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Logo Text (Single Character)
                    </label>
                    <input
                      type="text"
                      maxLength="1"
                      value={designConfig.logo_text}
                      onChange={(e) => handleChange('logo_text', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-center text-2xl font-bold"
                      placeholder="E"
                    />
                    <p className="mt-2 text-xs text-gray-500">Used as fallback if no logo image is uploaded</p>
                  </div>
                </div>

                {/* Logo Upload Section */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Website Logo</h3>
                  <p className="text-sm text-gray-600 mb-4">Upload your company logo to display in the website header. Recommended size: 200x60px or similar aspect ratio. If no logo is uploaded, the Logo Text will be used.</p>
                  
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    {/* Logo Preview */}
                    <div className="flex-shrink-0">
                      {designConfig.logo_image ? (
                        <div className="relative">
                          <img
                            src={designConfig.logo_image}
                            alt="Website Logo"
                            className="max-w-[200px] max-h-[80px] object-contain border-2 border-gray-300 rounded-lg p-2 bg-white"
                          />
                          <button
                            onClick={() => handleChange('logo_image', '')}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            title="Remove logo"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <div className="w-[200px] h-[80px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-white">
                          <div 
                            className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
                            style={{ 
                              background: `linear-gradient(135deg, ${designConfig.primary_color || '#4F46E5'}, ${designConfig.accent_color || '#EC4899'})`
                            }}
                          >
                            <span className="text-white font-light text-2xl tracking-wide" style={{ fontFamily: 'serif' }}>
                              {designConfig.logo_text || 'E'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 ml-2">Preview (Logo Text)</p>
                        </div>
                      )}
                    </div>

                    {/* Upload Button */}
                    <div className="flex-1">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Upload Logo Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleLogoUpload(file);
                          }
                        }}
                        className="hidden"
                        id="logo-upload"
                        disabled={uploadingImages.logo}
                      />
                      <label
                        htmlFor="logo-upload"
                        className={`
                          inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl cursor-pointer hover:bg-indigo-700 transition-colors font-semibold
                            w-full justify-center sm:w-auto
                          ${uploadingImages.logo ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                      >
                        {uploadingImages.logo ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <PhotoIcon className="w-5 h-5" />
                            {designConfig.logo_image ? 'Change Logo' : 'Upload Logo'}
                          </>
                        )}
                      </label>
                      <p className="mt-2 text-xs text-gray-500">Supported formats: JPG, PNG, GIF, SVG. Max size: 5MB</p>
                      {designConfig.logo_image && (
                        <button
                          onClick={() => handleChange('logo_image', '')}
                          className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium"
                        >
                          Remove Logo (use Logo Text instead)
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    About Us Text
                  </label>
                  <textarea
                    value={designConfig.about_text}
                    onChange={(e) => handleChange('about_text', e.target.value)}
                    rows="4"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Write about your store..."
                  />
                </div>
              </div>
            )}

            {/* Colors Tab */}
            {activeTab === 'colors' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Color Scheme</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    { key: 'primary_color', label: 'Primary Color', desc: 'Main brand color' },
                    { key: 'secondary_color', label: 'Secondary Color', desc: 'Secondary brand color' },
                    { key: 'accent_color', label: 'Accent Color', desc: 'Highlight color' },
                    { key: 'background_color', label: 'Background Color', desc: 'Page background' },
                    { key: 'text_color', label: 'Text Color', desc: 'Main text color' },
                    { key: 'header_bg', label: 'Header Background', desc: 'Header background' },
                    { key: 'footer_bg', label: 'Footer Background', desc: 'Footer background' },
                    { key: 'footer_text', label: 'Footer Text', desc: 'Footer text color' }
                  ].map((color) => (
                    <div key={color.key} className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        {color.label}
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={designConfig[color.key]}
                          onChange={(e) => handleChange(color.key, e.target.value)}
                          className="w-16 h-16 rounded-lg border-2 border-gray-300 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={designConfig[color.key]}
                          onChange={(e) => handleChange(color.key, e.target.value)}
                          className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                          placeholder="#000000"
                        />
                      </div>
                      <p className="text-xs text-gray-500">{color.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hero Section Tab */}
            {activeTab === 'hero' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Hero Section</h2>

                {/* Home: Special Offer Banner */}
                <div className="border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Special Offer Banner Image</h3>
                  <div className="space-y-4">
                    <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <input
                        type="checkbox"
                        checked={(designConfig.special_offer_enabled || 'true') === 'true'}
                        onChange={(e) => handleChange('special_offer_enabled', e.target.checked ? 'true' : 'false')}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-semibold text-gray-700">Enable Special Offer section</span>
                    </label>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Upload Banner Image (Home → All Products)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length > 0) handleSpecialOfferBannerUpload(files);
                        }}
                        disabled={uploadingImages.special_offer}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      {uploadingImages.special_offer && (
                        <p className="text-sm text-blue-600 mt-2">Uploading image...</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Upload multiple images. They rotate automatically on the Home special offer banner.</p>
                    </div>

                    {designConfig.special_offer_banner_image && designConfig.special_offer_banner_image.trim() && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Current Banner Preview</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {designConfig.special_offer_banner_image
                            .split(',')
                            .map((url) => url.trim())
                            .filter((url) => url)
                            .map((imageUrl, index) => (
                              <div key={`special-offer-${index}`} className="relative group">
                                <img
                                  src={imageUrl}
                                  alt={`Special offer ${index + 1}`}
                                  className="w-full h-28 object-cover rounded-lg border-2 border-gray-200"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeImage('special_offer', index)}
                                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Auto-rotate interval (ms)</label>
                        <input
                          type="number"
                          min="1500"
                          step="500"
                          value={designConfig.special_offer_autoplay_speed || '4000'}
                          onChange={(e) => handleChange('special_offer_autoplay_speed', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="4000"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Auto-rotate direction</label>
                        <select
                          value={designConfig.special_offer_scroll_direction || 'rtl'}
                          onChange={(e) => handleChange('special_offer_scroll_direction', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="rtl">Right to Left</option>
                          <option value="ltr">Left to Right</option>
                        </select>
                      </div>

                      <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={(designConfig.special_offer_autoplay || 'true') === 'true'}
                          onChange={(e) => handleChange('special_offer_autoplay', e.target.checked ? 'true' : 'false')}
                          className="w-4 h-4"
                        />
                        <span className="text-sm font-semibold text-gray-700">Enable auto-rotate</span>
                      </label>

                      <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={(designConfig.special_offer_swipe_enabled || 'true') === 'true'}
                          onChange={(e) => handleChange('special_offer_swipe_enabled', e.target.checked ? 'true' : 'false')}
                          className="w-4 h-4"
                        />
                        <span className="text-sm font-semibold text-gray-700">Enable swipe/drag on mobile</span>
                      </label>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Label</label>
                        <input
                          type="text"
                          value={designConfig.special_offer_label || ''}
                          onChange={(e) => handleChange('special_offer_label', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="SPECIAL OFFER"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Percent Text</label>
                        <input
                          type="text"
                          value={designConfig.special_offer_percent_text || ''}
                          onChange={(e) => handleChange('special_offer_percent_text', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="10%"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">OFF Text</label>
                        <input
                          type="text"
                          value={designConfig.special_offer_off_text || ''}
                          onChange={(e) => handleChange('special_offer_off_text', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="OFF"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Button Text</label>
                        <input
                          type="text"
                          value={designConfig.special_offer_button_text || ''}
                          onChange={(e) => handleChange('special_offer_button_text', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Shop Now"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Button Link (path)</label>
                        <input
                          type="text"
                          value={designConfig.special_offer_button_link || ''}
                          onChange={(e) => handleChange('special_offer_button_link', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="/products"
                        />
                        <p className="text-xs text-gray-500 mt-1">Use an internal path like /products or /products?category=1</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Products: Hero Section */}
                <div className="border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Products Page Hero</h3>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={(designConfig.products_hero_enabled || 'true') === 'true'}
                          onChange={(e) => handleChange('products_hero_enabled', e.target.checked ? 'true' : 'false')}
                          className="w-4 h-4"
                        />
                        <span className="text-sm font-semibold text-gray-700">Enable hero section</span>
                      </label>

                      <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={(designConfig.products_hero_use_image || 'false') === 'true'}
                          onChange={(e) => handleChange('products_hero_use_image', e.target.checked ? 'true' : 'false')}
                          className="w-4 h-4"
                        />
                        <span className="text-sm font-semibold text-gray-700">Use uploaded background image</span>
                      </label>

                      <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <input
                          type="checkbox"
                          checked={(designConfig.products_hero_overlay_enabled || 'true') === 'true'}
                          onChange={(e) => handleChange('products_hero_overlay_enabled', e.target.checked ? 'true' : 'false')}
                          className="w-4 h-4"
                        />
                        <span className="text-sm font-semibold text-gray-700">Enable dark overlay on image</span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Upload Background Image (Products page)</label>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          if (files.length > 0) handleProductsHeroBannerUpload(files);
                        }}
                        disabled={uploadingImages.products_hero}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      {uploadingImages.products_hero && (
                        <p className="text-sm text-blue-600 mt-2">Uploading image...</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">Upload multiple images. They rotate automatically on the Products page hero.</p>
                    </div>

                    {designConfig.products_hero_background_image && designConfig.products_hero_background_image.trim() && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Current Products Hero Preview</label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {designConfig.products_hero_background_image
                              .split(',')
                              .map((url) => url.trim())
                              .filter((url) => url)
                              .map((imageUrl, index) => (
                                <div key={`products-hero-${index}`} className="relative group">
                                  <img
                                    src={imageUrl}
                                    alt={`Products hero ${index + 1}`}
                                    className="w-full h-28 object-cover rounded-lg border-2 border-gray-200"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeImage('products_hero', index)}
                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Remove"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Auto-rotate interval (ms)</label>
                        <input
                          type="number"
                          min="1500"
                          step="500"
                          value={designConfig.products_hero_autoplay_speed || '4000'}
                          onChange={(e) => handleChange('products_hero_autoplay_speed', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="4000"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Auto-rotate direction</label>
                        <select
                          value={designConfig.products_hero_scroll_direction || 'rtl'}
                          onChange={(e) => handleChange('products_hero_scroll_direction', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="rtl">Right to Left</option>
                          <option value="ltr">Left to Right</option>
                        </select>
                      </div>

                      <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg md:col-span-1">
                        <input
                          type="checkbox"
                          checked={(designConfig.products_hero_autoplay || 'true') === 'true'}
                          onChange={(e) => handleChange('products_hero_autoplay', e.target.checked ? 'true' : 'false')}
                          className="w-4 h-4"
                        />
                        <span className="text-sm font-semibold text-gray-700">Enable auto-rotate</span>
                      </label>

                      <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg md:col-span-1">
                        <input
                          type="checkbox"
                          checked={(designConfig.products_hero_swipe_enabled || 'true') === 'true'}
                          onChange={(e) => handleChange('products_hero_swipe_enabled', e.target.checked ? 'true' : 'false')}
                          className="w-4 h-4"
                        />
                        <span className="text-sm font-semibold text-gray-700">Enable swipe/drag on mobile</span>
                      </label>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Top Label (optional)</label>
                        <input
                          type="text"
                          value={designConfig.products_hero_top_label || ''}
                          onChange={(e) => handleChange('products_hero_top_label', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Collection"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                        <input
                          type="text"
                          value={designConfig.products_hero_title || ''}
                          onChange={(e) => handleChange('products_hero_title', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Our Collection"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Subtitle</label>
                        <textarea
                          rows="3"
                          value={designConfig.products_hero_subtitle || ''}
                          onChange={(e) => handleChange('products_hero_subtitle', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Discover our curated selection..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Use Images Toggle */}
                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="hero_use_images"
                    checked={designConfig.hero_use_images === 'true'}
                    onChange={(e) => handleChange('hero_use_images', e.target.checked ? 'true' : 'false')}
                    className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="hero_use_images" className="text-sm font-semibold text-gray-700">
                    Use Images in Hero Section (Uncheck to use text only)
                  </label>
                </div>

                {designConfig.hero_use_images === 'true' ? (
                  <div className="space-y-6">
                    {/* Top Banner Section */}
                    <div className="border-2 border-gray-200 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Banner (H1)</h3>
                      <div className="space-y-4">
                        <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={(designConfig.hero_banner_enabled || 'true') === 'true'}
                            onChange={(e) => handleChange('hero_banner_enabled', e.target.checked ? 'true' : 'false')}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-gray-700">Enable Top Banner (H1)</span>
                        </label>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Upload Banner Images (for carousel)
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              if (files.length > 0) handleHeroImageUpload(files, 'banner');
                            }}
                            disabled={uploadingImages.banner}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          {uploadingImages.banner && (
                            <p className="text-sm text-blue-600 mt-2">Uploading image...</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">Upload multiple images to create a rotating carousel</p>
                        </div>
                        
                        {/* Display uploaded banner images */}
                        {designConfig.hero_banner_images && designConfig.hero_banner_images.trim() && (
                          <div className="mt-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Banner Images Preview
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {designConfig.hero_banner_images
                                .split(',')
                                .map((url) => normalizeUploadUrl(url))
                                .filter((url) => url)
                                .map((trimmedUrl, index) => {
                                return (
                                  <div key={index} className="relative group">
                                    <img
                                      src={trimmedUrl}
                                      alt={`Banner ${index + 1}`}
                                      className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeImage('banner', index)}
                                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      ×
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Auto-rotate interval (ms)</label>
                          <input
                            type="number"
                            min="1500"
                            step="500"
                            value={designConfig.hero_banner_autoplay_speed || '4000'}
                            onChange={(e) => handleChange('hero_banner_autoplay_speed', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="4000"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Auto-rotate direction</label>
                          <select
                            value={designConfig.hero_banner_scroll_direction || 'rtl'}
                            onChange={(e) => handleChange('hero_banner_scroll_direction', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="rtl">Right to Left</option>
                            <option value="ltr">Left to Right</option>
                          </select>
                        </div>

                        <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={(designConfig.hero_banner_autoplay || 'true') === 'true'}
                            onChange={(e) => handleChange('hero_banner_autoplay', e.target.checked ? 'true' : 'false')}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-gray-700">Enable auto-rotate</span>
                        </label>

                        <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={(designConfig.hero_banner_swipe_enabled || 'true') === 'true'}
                            onChange={(e) => handleChange('hero_banner_swipe_enabled', e.target.checked ? 'true' : 'false')}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-gray-700">Enable swipe/drag on mobile</span>
                        </label>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Banner Text (shown if no images)
                          </label>
                          <input
                            type="text"
                            value={designConfig.hero_banner_text || ''}
                            onChange={(e) => handleChange('hero_banner_text', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Welcome to Our Store"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Left Section (60% width) */}
                    <div className="border-2 border-gray-200 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Left Section (H2 - 60% width)</h3>
                      <div className="space-y-4">
                        <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={(designConfig.hero_left_enabled || 'true') === 'true'}
                            onChange={(e) => handleChange('hero_left_enabled', e.target.checked ? 'true' : 'false')}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-gray-700">Enable Left Section (H2)</span>
                        </label>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Upload Left Section Image
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              if (files.length > 0) handleHeroImageUpload(files, 'left');
                            }}
                            disabled={uploadingImages.left}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          {uploadingImages.left && (
                            <p className="text-sm text-blue-600 mt-2">Uploading image...</p>
                          )}
                        </div>

                        {/* Preview left images */}
                        {designConfig.hero_left_image && designConfig.hero_left_image.trim() && (
                          <div className="mt-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Left Section Images Preview {designConfig.hero_left_image.split(',').filter(url => url.trim()).length > 1 && '(Multiple images will auto-rotate)'}
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              {designConfig.hero_left_image.split(',').map((url, index) => {
                                const trimmedUrl = url.trim();
                                if (!trimmedUrl) return null;
                                return (
                                  <div key={index} className="relative group">
                                    <img
                                      src={trimmedUrl}
                                      alt={`Left section ${index + 1}`}
                                      className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeImage('left', index)}
                                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      ×
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Auto-rotate interval (ms)</label>
                          <input
                            type="number"
                            min="1500"
                            step="500"
                            value={designConfig.hero_left_autoplay_speed || '4000'}
                            onChange={(e) => handleChange('hero_left_autoplay_speed', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="4000"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Auto-rotate direction</label>
                          <select
                            value={designConfig.hero_left_scroll_direction || 'rtl'}
                            onChange={(e) => handleChange('hero_left_scroll_direction', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="rtl">Right to Left</option>
                            <option value="ltr">Left to Right</option>
                          </select>
                        </div>

                        <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={(designConfig.hero_left_autoplay || 'true') === 'true'}
                            onChange={(e) => handleChange('hero_left_autoplay', e.target.checked ? 'true' : 'false')}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-gray-700">Enable auto-rotate</span>
                        </label>

                        <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={(designConfig.hero_left_swipe_enabled || 'true') === 'true'}
                            onChange={(e) => handleChange('hero_left_swipe_enabled', e.target.checked ? 'true' : 'false')}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-gray-700">Enable swipe/drag on mobile</span>
                        </label>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Left Section Text (shown if no image)
                          </label>
                          <input
                            type="text"
                            value={designConfig.hero_left_text || ''}
                            onChange={(e) => handleChange('hero_left_text', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Discover Premium Collection"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Left Section Minimum Height (px)
                          </label>
                          <input
                            type="number"
                            min="150"
                            max="800"
                            value={designConfig.hero_left_min_height || '300'}
                            onChange={(e) => handleChange('hero_left_min_height', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="300"
                          />
                          <p className="text-xs text-gray-500 mt-1">Controls the minimum height of the left section on all screen sizes (150-800px)</p>
                        </div>
                      </div>
                    </div>

                    {/* Right Section (40% width) */}
                    <div className="border-2 border-gray-200 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Right Section (H3 - 40% width)</h3>
                      <div className="space-y-4">
                        <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={(designConfig.hero_right_enabled || 'true') === 'true'}
                            onChange={(e) => handleChange('hero_right_enabled', e.target.checked ? 'true' : 'false')}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-gray-700">Enable Right Section (H3)</span>
                        </label>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Upload Right Section Image
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              if (files.length > 0) handleHeroImageUpload(files, 'right');
                            }}
                            disabled={uploadingImages.right}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                          {uploadingImages.right && (
                            <p className="text-sm text-blue-600 mt-2">Uploading image...</p>
                          )}
                        </div>

                        {/* Preview right images */}
                        {designConfig.hero_right_image && designConfig.hero_right_image.trim() && (
                          <div className="mt-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Right Section Images Preview {designConfig.hero_right_image.split(',').filter(url => url.trim()).length > 1 && '(Multiple images will auto-rotate)'}
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              {designConfig.hero_right_image.split(',').map((url, index) => {
                                const trimmedUrl = url.trim();
                                if (!trimmedUrl) return null;
                                return (
                                  <div key={index} className="relative group">
                                    <img
                                      src={trimmedUrl}
                                      alt={`Right section ${index + 1}`}
                                      className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeImage('right', index)}
                                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      ×
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Auto-rotate interval (ms)</label>
                          <input
                            type="number"
                            min="1500"
                            step="500"
                            value={designConfig.hero_right_autoplay_speed || '4000'}
                            onChange={(e) => handleChange('hero_right_autoplay_speed', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="4000"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Auto-rotate direction</label>
                          <select
                            value={designConfig.hero_right_scroll_direction || 'rtl'}
                            onChange={(e) => handleChange('hero_right_scroll_direction', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="rtl">Right to Left</option>
                            <option value="ltr">Left to Right</option>
                          </select>
                        </div>

                        <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={(designConfig.hero_right_autoplay || 'true') === 'true'}
                            onChange={(e) => handleChange('hero_right_autoplay', e.target.checked ? 'true' : 'false')}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-gray-700">Enable auto-rotate</span>
                        </label>

                        <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="checkbox"
                            checked={(designConfig.hero_right_swipe_enabled || 'true') === 'true'}
                            onChange={(e) => handleChange('hero_right_swipe_enabled', e.target.checked ? 'true' : 'false')}
                            className="w-4 h-4"
                          />
                          <span className="text-sm font-semibold text-gray-700">Enable swipe/drag on mobile</span>
                        </label>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Right Section Text (shown if no image)
                          </label>
                          <input
                            type="text"
                            value={designConfig.hero_right_text || ''}
                            onChange={(e) => handleChange('hero_right_text', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="New Arrivals"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Right Section Minimum Height (px)
                          </label>
                          <input
                            type="number"
                            min="150"
                            max="800"
                            value={designConfig.hero_right_min_height || '300'}
                            onChange={(e) => handleChange('hero_right_min_height', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="300"
                          />
                          <p className="text-xs text-gray-500 mt-1">Controls the minimum height of the right section on all screen sizes (150-800px)</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Hero Title
                      </label>
                      <input
                        type="text"
                        value={designConfig.hero_title}
                        onChange={(e) => handleChange('hero_title', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Luxury E-Commerce"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Hero Subtitle
                      </label>
                      <textarea
                        value={designConfig.hero_subtitle}
                        onChange={(e) => handleChange('hero_subtitle', e.target.value)}
                        rows="3"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Your hero subtitle..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Hero Button Text
                      </label>
                      <input
                        type="text"
                        value={designConfig.hero_button_text}
                        onChange={(e) => handleChange('hero_button_text', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Explore Collection"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Features Tab */}
            {activeTab === 'features' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Feature Section</h2>
                
                {[1, 2, 3].map((num) => (
                  <div key={num} className="border-2 border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Feature {num}</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Title
                        </label>
                        <input
                          type="text"
                          value={designConfig[`feature_${num}_title`]}
                          onChange={(e) => handleChange(`feature_${num}_title`, e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          value={designConfig[`feature_${num}_description`]}
                          onChange={(e) => handleChange(`feature_${num}_description`, e.target.value)}
                          rows="2"
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Footer Tab */}
            {activeTab === 'footer' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Footer Configuration</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Footer About Text
                    </label>
                    <textarea
                      value={designConfig.footer_about}
                      onChange={(e) => handleChange('footer_about', e.target.value)}
                      rows="3"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Copyright Text
                  </label>
                    <input
                      type="text"
                      value={designConfig.footer_copyright}
                      onChange={(e) => handleChange('footer_copyright', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={designConfig.contact_email}
                        onChange={(e) => handleChange('contact_email', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Contact Phone
                      </label>
                      <input
                        type="tel"
                        value={designConfig.contact_phone}
                        onChange={(e) => handleChange('contact_phone', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-4">
                      Social Media Links
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {['facebook', 'twitter', 'instagram', 'linkedin'].map((social) => (
                        <div key={social}>
                          <label className="block text-xs text-gray-600 mb-1 capitalize">
                            {social}
                          </label>
                          <input
                            type="url"
                            value={designConfig[`social_${social}`]}
                            onChange={(e) => handleChange(`social_${social}`, e.target.value)}
                            className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder={`https://${social}.com/yourpage`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Announcement Banner Tab */}
            {activeTab === 'announcement' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Announcement Banner</h2>
                <p className="text-gray-600 mb-6">Configure the moving announcement banner that appears at the top of your site.</p>
                
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <label className="flex items-start gap-2 text-lg font-semibold text-gray-900 sm:items-center">
                      <input
                        type="checkbox"
                        checked={designConfig.announcement_enabled}
                        onChange={(e) => handleChange('announcement_enabled', e.target.checked)}
                        className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      Enable Announcement Banner
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">When enabled, the banner will scroll from right to left across the top of your site.</p>
                </div>

                {designConfig.announcement_enabled && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Banner Message *
                      </label>
                      <textarea
                        value={designConfig.announcement_text}
                        onChange={(e) => handleChange('announcement_text', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Enter your announcement message..."
                      />
                      <p className="mt-2 text-xs text-gray-500">This text will scroll from right to left</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Text Color
                        </label>
                        <div className="flex gap-3">
                          <input
                            type="color"
                            value={designConfig.announcement_text_color}
                            onChange={(e) => handleChange('announcement_text_color', e.target.value)}
                            className="w-16 h-12 border-2 border-gray-300 rounded-lg cursor-pointer"
                          />
                          <input
                            type="text"
                            value={designConfig.announcement_text_color}
                            onChange={(e) => handleChange('announcement_text_color', e.target.value)}
                            className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="#ffffff"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Background Color
                        </label>
                        <div className="flex gap-3">
                          <input
                            type="color"
                            value={designConfig.announcement_bg_color}
                            onChange={(e) => handleChange('announcement_bg_color', e.target.value)}
                            className="w-16 h-12 border-2 border-gray-300 rounded-lg cursor-pointer"
                          />
                          <input
                            type="text"
                            value={designConfig.announcement_bg_color}
                            onChange={(e) => handleChange('announcement_bg_color', e.target.value)}
                            className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="#000000"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Animation Speed
                      </label>
                      <select
                        value={designConfig.announcement_speed}
                        onChange={(e) => handleChange('announcement_speed', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="slow">Slow</option>
                        <option value="medium">Medium</option>
                        <option value="fast">Fast</option>
                      </select>
                    </div>

                    <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Preview</h3>
                      <div
                        className="relative overflow-hidden py-2 text-xs md:text-sm rounded"
                        style={{ 
                          backgroundColor: designConfig.announcement_bg_color, 
                          color: designConfig.announcement_text_color 
                        }}
                      >
                        <div className="px-4 whitespace-nowrap">
                          {designConfig.announcement_text || 'Your announcement message will appear here...'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Promotions Tab */}
            {activeTab === 'promotions' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Promotions</h2>
                <p className="text-gray-600">
                  Configure multiple promo codes with a versioned rules model. Use Promo Rules v2 for bundle offers like bag + bottle + tiffin, and keep the legacy tiered promo only if you still need it.
                </p>

                <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-6">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Promo Rules v{promoRulesConfig.version}</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Each rule is its own promo code. The current implementation supports bundle validation for specific bag, bottle, and tiffin products.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={addPromoRule}
                      className="rounded-xl bg-emerald-600 px-4 py-2 font-semibold text-white hover:bg-emerald-700"
                    >
                      Add Promo Rule
                    </button>
                  </div>
                </div>

                {promoRulesConfig.promos.length === 0 ? (
                  <div className="rounded-xl border-2 border-dashed border-gray-300 bg-white p-6 text-sm text-gray-600">
                    No promo rules added yet. Add a promo rule for offers such as Back to School.
                  </div>
                ) : null}

                {promoRulesConfig.promos.map((promo, index) => (
                  <div key={promo.id} className="rounded-xl border-2 border-gray-200 bg-white p-6 shadow-sm">
                    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Promo Rule {index + 1}</h3>
                        <p className="text-sm text-gray-600">Versioned rule using promo type <span className="font-semibold">{promo.type}</span>.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePromoRule(promo.id)}
                        className="rounded-xl bg-red-50 px-4 py-2 font-semibold text-red-600 hover:bg-red-100"
                      >
                        Remove Rule
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-700">Promo Code</label>
                        <input
                          type="text"
                          value={promo.code}
                          onChange={(e) => updatePromoRule(promo.id, 'code', e.target.value.toUpperCase())}
                          className="w-full rounded-xl border-2 border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="BACKTOSCHOOL"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-700">Promo Name</label>
                        <input
                          type="text"
                          value={promo.name}
                          onChange={(e) => updatePromoRule(promo.id, 'name', e.target.value)}
                          className="w-full rounded-xl border-2 border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="Back to School Combo"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-700">Minimum Order Amount</label>
                        <input
                          type="number"
                          min="0"
                          value={promo.minOrderAmount}
                          onChange={(e) => updatePromoRule(promo.id, 'minOrderAmount', e.target.value)}
                          className="w-full rounded-xl border-2 border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-700">Max Discount (optional)</label>
                        <input
                          type="number"
                          min="0"
                          value={promo.maxDiscount}
                          onChange={(e) => updatePromoRule(promo.id, 'maxDiscount', e.target.value)}
                          className="w-full rounded-xl border-2 border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-700">Start Date</label>
                        <input
                          type="date"
                          value={promo.startDate}
                          onChange={(e) => updatePromoRule(promo.id, 'startDate', e.target.value)}
                          className="w-full rounded-xl border-2 border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-semibold text-gray-700">End Date</label>
                        <input
                          type="date"
                          value={promo.endDate}
                          onChange={(e) => updatePromoRule(promo.id, 'endDate', e.target.value)}
                          className="w-full rounded-xl border-2 border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                      <label className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">
                        <input
                          type="checkbox"
                          checked={promo.enabled}
                          onChange={(e) => updatePromoRule(promo.id, 'enabled', e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                        />
                        Enabled
                      </label>

                      <label className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">
                        <input
                          type="checkbox"
                          checked={promo.repeatable}
                          onChange={(e) => updatePromoRule(promo.id, 'repeatable', e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                        />
                        Repeatable per order
                      </label>

                      <label className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">
                        <input
                          type="checkbox"
                          checked={promo.stackable}
                          onChange={(e) => updatePromoRule(promo.id, 'stackable', e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                        />
                        Stackable
                      </label>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div className="rounded-xl border border-gray-200 p-4">
                        <h4 className="mb-1 text-base font-semibold text-gray-900">Buy Conditions</h4>
                        <p className="mb-4 text-xs text-gray-600">Customer must have one eligible bag, one eligible bottle, and one eligible tiffin in cart.</p>

                        <label className="mb-2 block text-sm font-semibold text-gray-700">Eligible Bag Product IDs</label>
                        <textarea
                          rows={4}
                          value={promo.bagProductIdsText}
                          onChange={(e) => updatePromoRule(promo.id, 'bagProductIdsText', e.target.value)}
                          className="mb-4 w-full rounded-xl border-2 border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="101\n102"
                        />

                        <label className="mb-2 block text-sm font-semibold text-gray-700">Eligible Bottle Product IDs</label>
                        <textarea
                          rows={4}
                          value={promo.bottleProductIdsText}
                          onChange={(e) => updatePromoRule(promo.id, 'bottleProductIdsText', e.target.value)}
                          className="mb-4 w-full rounded-xl border-2 border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="201\n202"
                        />

                        <label className="mb-2 block text-sm font-semibold text-gray-700">Eligible Tiffin Product IDs</label>
                        <textarea
                          rows={4}
                          value={promo.tiffinProductIdsText}
                          onChange={(e) => updatePromoRule(promo.id, 'tiffinProductIdsText', e.target.value)}
                          className="w-full rounded-xl border-2 border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="301\n302"
                        />
                      </div>

                      <div className="rounded-xl border border-gray-200 p-4">
                        <h4 className="mb-1 text-base font-semibold text-gray-900">Reward Items</h4>
                        <p className="mb-4 text-xs text-gray-600">Only these specific bottle and tiffin products can become free.</p>

                        <label className="mb-2 block text-sm font-semibold text-gray-700">Reward Bottle Product IDs</label>
                        <textarea
                          rows={4}
                          value={promo.rewardBottleProductIdsText}
                          onChange={(e) => updatePromoRule(promo.id, 'rewardBottleProductIdsText', e.target.value)}
                          className="mb-4 w-full rounded-xl border-2 border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="201\n202"
                        />

                        <label className="mb-2 block text-sm font-semibold text-gray-700">Reward Tiffin Product IDs</label>
                        <textarea
                          rows={4}
                          value={promo.rewardTiffinProductIdsText}
                          onChange={(e) => updatePromoRule(promo.id, 'rewardTiffinProductIdsText', e.target.value)}
                          className="w-full rounded-xl border-2 border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="301\n302"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleSavePromoRules}
                    disabled={savingPromoRules}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-6 py-3 font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 sm:w-auto"
                  >
                    <CheckCircleIcon className="w-5 h-5" />
                    {savingPromoRules ? 'Saving Promo Rules...' : 'Save Promo Rules v2'}
                  </button>
                </div>

                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                  <label className="flex items-start gap-2 text-lg font-semibold text-gray-900 sm:items-center">
                    <input
                      type="checkbox"
                      checked={tieredPromoConfig.enabled}
                      onChange={(e) => setTieredPromoConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                      className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    Enable Tiered Promo Code
                  </label>
                  <p className="text-sm text-gray-600 mt-2">
                    Redemption rules: counts <span className="font-semibold">DELIVERED</span> orders only and allows <span className="font-semibold">once per tier per user</span>.
                  </p>
                  <p className="mt-2 text-xs text-blue-700">
                    Legacy mode. Keep this only if you still need the original single-code percentage promo behavior.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Promo Code</label>
                    <input
                      type="text"
                      value={tieredPromoConfig.code}
                      onChange={(e) => setTieredPromoConfig(prev => ({ ...prev, code: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="FIRST10"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Minimum Order Amount</label>
                    <input
                      type="number"
                      value={tieredPromoConfig.minOrderAmount}
                      onChange={(e) => setTieredPromoConfig(prev => ({ ...prev, minOrderAmount: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="0"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={tieredPromoConfig.startDate || ''}
                      onChange={(e) => setTieredPromoConfig(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={tieredPromoConfig.endDate || ''}
                      onChange={(e) => setTieredPromoConfig(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Max Discount (optional)</label>
                    <input
                      type="number"
                      value={tieredPromoConfig.maxDiscount}
                      onChange={(e) => setTieredPromoConfig(prev => ({ ...prev, maxDiscount: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder=""
                      min="0"
                    />
                  </div>
                </div>

                <div className="border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Tier Discounts (%)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">1st Purchase</label>
                      <input
                        type="number"
                        value={tieredPromoConfig.tier1Percent}
                        onChange={(e) => setTieredPromoConfig(prev => ({ ...prev, tier1Percent: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        min="0"
                        max="100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">2nd Purchase</label>
                      <input
                        type="number"
                        value={tieredPromoConfig.tier2Percent}
                        onChange={(e) => setTieredPromoConfig(prev => ({ ...prev, tier2Percent: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        min="0"
                        max="100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">3rd Purchase</label>
                      <input
                        type="number"
                        value={tieredPromoConfig.tier3Percent}
                        onChange={(e) => setTieredPromoConfig(prev => ({ ...prev, tier3Percent: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="border-2 border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Allowed Category IDs (optional)</h3>
                    <p className="text-xs text-gray-600 mb-3">One per line (or comma separated). Leave empty to allow all.</p>
                    <textarea
                      value={tieredPromoConfig.allowedCategoryIdsText}
                      onChange={(e) => setTieredPromoConfig(prev => ({ ...prev, allowedCategoryIdsText: e.target.value }))}
                      rows={5}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="1\n2\n3"
                    />
                  </div>

                  <div className="border-2 border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Allowed Product IDs (optional)</h3>
                    <p className="text-xs text-gray-600 mb-3">One per line (or comma separated). Leave empty to allow all.</p>
                    <textarea
                      value={tieredPromoConfig.allowedProductIdsText}
                      onChange={(e) => setTieredPromoConfig(prev => ({ ...prev, allowedProductIdsText: e.target.value }))}
                      rows={5}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="101\n102"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveTieredPromo}
                    disabled={savingPromo}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 sm:w-auto"
                  >
                    <CheckCircleIcon className="w-5 h-5" />
                    {savingPromo ? 'Saving...' : 'Save Promo Settings'}
                  </button>
                </div>
              </div>
            )}

            {/* Invoice Design Tab */}
            {activeTab === 'invoice' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Invoice Design Settings</h2>
                <p className="text-gray-600 mb-6">Configure your invoice appearance, company information, and branding for printed invoices.</p>
                
                {/* Company Information */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Company Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        value={designConfig.invoice_company_name}
                        onChange={(e) => handleChange('invoice_company_name', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Your Company Name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Company Email
                      </label>
                      <input
                        type="email"
                        value={designConfig.invoice_company_email}
                        onChange={(e) => handleChange('invoice_company_email', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="info@company.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Company Phone
                      </label>
                      <input
                        type="text"
                        value={designConfig.invoice_company_phone}
                        onChange={(e) => handleChange('invoice_company_phone', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Company Website
                      </label>
                      <input
                        type="url"
                        value={designConfig.invoice_company_website}
                        onChange={(e) => handleChange('invoice_company_website', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="https://www.company.com"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Company Address
                      </label>
                      <input
                        type="text"
                        value={designConfig.invoice_company_address}
                        onChange={(e) => handleChange('invoice_company_address', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Street Address"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        City
                      </label>
                      <input
                        type="text"
                        value={designConfig.invoice_company_city}
                        onChange={(e) => handleChange('invoice_company_city', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="City"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        State / Province
                      </label>
                      <input
                        type="text"
                        value={designConfig.invoice_company_state}
                        onChange={(e) => handleChange('invoice_company_state', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="State"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        ZIP / Postal Code
                      </label>
                      <input
                        type="text"
                        value={designConfig.invoice_company_zip}
                        onChange={(e) => handleChange('invoice_company_zip', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="12345"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Country
                      </label>
                      <input
                        type="text"
                        value={designConfig.invoice_company_country}
                        onChange={(e) => handleChange('invoice_company_country', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Country"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Tax ID / EIN
                      </label>
                      <input
                        type="text"
                        value={designConfig.invoice_tax_id}
                        onChange={(e) => handleChange('invoice_tax_id', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Tax ID Number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        GST Number
                      </label>
                      <input
                        type="text"
                        value={designConfig.invoice_gst_number}
                        onChange={(e) => handleChange('invoice_gst_number', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="GST Number"
                      />
                    </div>
                  </div>
                </div>

                {/* Logo Upload */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Company Logo</h3>
                  <p className="text-sm text-gray-600 mb-4">Upload your company logo to display on invoices. Recommended size: 200x80px or similar aspect ratio.</p>
                  
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    {/* Logo Preview */}
                    <div className="flex-shrink-0">
                      {designConfig.invoice_logo ? (
                        <div className="relative">
                          <img
                            src={designConfig.invoice_logo}
                            alt="Invoice Logo"
                            className="max-w-[200px] max-h-[100px] object-contain border-2 border-gray-300 rounded-lg p-2 bg-white"
                          />
                          <button
                            onClick={() => handleChange('invoice_logo', '')}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            title="Remove logo"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <div className="w-[200px] h-[100px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                          <p className="text-xs text-gray-400 text-center px-2">No logo uploaded</p>
                        </div>
                      )}
                    </div>

                    {/* Upload Button */}
                    <div className="flex-1">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Upload Logo
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleInvoiceLogoUpload(file);
                          }
                        }}
                        className="hidden"
                        id="invoice-logo-upload"
                        disabled={uploadingImages.invoice_logo}
                      />
                      <label
                        htmlFor="invoice-logo-upload"
                        className={`
                          inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl cursor-pointer hover:bg-indigo-700 transition-colors font-semibold
                            w-full justify-center sm:w-auto
                          ${uploadingImages.invoice_logo ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                      >
                        {uploadingImages.invoice_logo ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <PhotoIcon className="w-5 h-5" />
                            {designConfig.invoice_logo ? 'Change Logo' : 'Upload Logo'}
                          </>
                        )}
                      </label>
                      <p className="mt-2 text-xs text-gray-500">Supported formats: JPG, PNG, GIF. Max size: 5MB</p>
                    </div>
                  </div>
                </div>

                {/* Invoice Footer & Terms */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Invoice Footer & Terms</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Footer Text
                      </label>
                      <textarea
                        value={designConfig.invoice_footer_text}
                        onChange={(e) => handleChange('invoice_footer_text', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Thank you for your business!"
                      />
                      <p className="mt-2 text-xs text-gray-500">This text appears at the bottom of the invoice</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Terms & Conditions
                      </label>
                      <textarea
                        value={designConfig.invoice_terms}
                        onChange={(e) => handleChange('invoice_terms', e.target.value)}
                        rows={4}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="All sales are final. Returns accepted within 30 days."
                      />
                      <p className="mt-2 text-xs text-gray-500">Terms and conditions that appear on the invoice</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Product Settings Tab */}
            {activeTab === 'product' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Product Settings</h2>
                <p className="text-gray-600 mb-6">Configure product image upload limits and other product-related settings.</p>
                
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Product Image Settings</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Minimum Images per Product *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={designConfig.product_image_min || 1}
                        onChange={(e) => handleChange('product_image_min', parseInt(e.target.value) || 1)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="1"
                      />
                      <p className="mt-2 text-xs text-gray-500">Minimum number of images required for each product</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Maximum Images per Product *
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={designConfig.product_image_max || 10}
                        onChange={(e) => handleChange('product_image_max', parseInt(e.target.value) || 10)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="10"
                      />
                      <p className="mt-2 text-xs text-gray-500">Maximum number of images allowed per product</p>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> These settings will be enforced when uploading product images. 
                      Products must have at least {designConfig.product_image_min || 1} image(s) and can have up to {designConfig.product_image_max || 10} image(s).
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'delivery' && (
              <div className="space-y-8">
                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Shipping Charges</h2>
                      <p className="text-gray-600 mt-1">Controls the shipping fee shown in Cart and Checkout.</p>
                    </div>
                    <button
                      onClick={handleSaveShipping}
                      disabled={savingShipping}
                      className={`w-full rounded-xl px-6 py-3 font-semibold text-white transition-all sm:w-auto ${
                        savingShipping ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                      }`}
                    >
                      {savingShipping ? 'Saving…' : 'Save Shipping'}
                    </button>
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Enable Shipping Fee</label>
                      <select
                        value={shippingConfig.enabled ? 'enabled' : 'disabled'}
                        onChange={(e) => setShippingConfig(prev => ({ ...prev, enabled: e.target.value === 'enabled' }))}
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                      >
                        <option value="enabled">Enabled</option>
                        <option value="disabled">Disabled (always free)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Flat Shipping Charge</label>
                      <input
                        type="number"
                        min="0"
                        value={shippingConfig.flatCharge}
                        onChange={(e) => setShippingConfig(prev => ({ ...prev, flatCharge: e.target.value }))}
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                        placeholder="500"
                      />
                      <p className="mt-2 text-xs text-gray-500">Applied when order subtotal is below the free-shipping threshold.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Free Shipping Over</label>
                      <input
                        type="number"
                        min="0"
                        value={shippingConfig.freeOver}
                        onChange={(e) => setShippingConfig(prev => ({ ...prev, freeOver: e.target.value }))}
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                        placeholder="5000"
                      />
                      <p className="mt-2 text-xs text-gray-500">If subtotal is greater than or equal to this, shipping is free.</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Delivery Serviceability (India)</h2>
                    <p className="text-gray-600 mt-1">
                      Controls the “out of delivery range” suggestion based on pincode. Used by <span className="font-mono">/api/geo/serviceability/:pincode</span>.
                    </p>
                  </div>

                  <button
                    onClick={handleSaveDelivery}
                    disabled={savingDelivery}
                    className={`w-full rounded-xl px-6 py-3 font-semibold text-white transition-all sm:w-auto ${
                      savingDelivery ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {savingDelivery ? 'Saving…' : 'Save Delivery Rules'}
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Serviceability Mode</h3>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Mode</label>
                    <select
                      value={deliveryConfig.mode}
                      onChange={(e) => setDeliveryConfig(prev => ({ ...prev, mode: e.target.value }))}
                      className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                    >
                      <option value="allowlist-with-deny">Allowlist with Deny Overrides (Recommended)</option>
                      <option value="allowlist">Allowlist Only</option>
                      <option value="denylist">Denylist Only</option>
                      <option value="all">All Serviceable (Disable checks)</option>
                    </select>

                    <div className="mt-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Allowlist pincodes</label>
                      <textarea
                        rows={8}
                        value={deliveryConfig.allowlistText}
                        onChange={(e) => setDeliveryConfig(prev => ({ ...prev, allowlistText: e.target.value }))}
                        placeholder="One pincode per line (e.g. 689662)"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-2">Only valid 6-digit pincodes are saved.</p>
                    </div>

                    <div className="mt-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Denylist pincodes (overrides)</label>
                      <textarea
                        rows={6}
                        value={deliveryConfig.denylistText}
                        onChange={(e) => setDeliveryConfig(prev => ({ ...prev, denylistText: e.target.value }))}
                        placeholder="One pincode per line"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Cash on Delivery (COD)</h3>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">COD mode</label>
                      <select
                        value={deliveryConfig.codMode}
                        onChange={(e) => setDeliveryConfig(prev => ({ ...prev, codMode: e.target.value }))}
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                      >
                        <option value="inherit">Inherit serviceability</option>
                        <option value="allowlist">Allowlist Only</option>
                        <option value="denylist">Denylist Only</option>
                        <option value="all">Allow COD everywhere</option>
                      </select>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">COD allowlist</label>
                          <textarea
                            rows={7}
                            value={deliveryConfig.codAllowlistText}
                            onChange={(e) => setDeliveryConfig(prev => ({ ...prev, codAllowlistText: e.target.value }))}
                            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono text-sm"
                            placeholder="One pincode per line"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">COD denylist</label>
                          <textarea
                            rows={7}
                            value={deliveryConfig.codDenylistText}
                            onChange={(e) => setDeliveryConfig(prev => ({ ...prev, codDenylistText: e.target.value }))}
                            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono text-sm"
                            placeholder="One pincode per line"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl p-6 border border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Test a pincode</h3>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="text"
                          value={deliveryTest.pincode}
                          onChange={(e) => setDeliveryTest(prev => ({ ...prev, pincode: e.target.value }))}
                          placeholder="Enter pincode (e.g. 689662)"
                          className="flex-1 px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 font-mono"
                        />
                        <button
                          onClick={handleTestPincode}
                          disabled={deliveryTest.loading}
                          className={`px-6 py-3 rounded-xl font-semibold text-white transition-all ${
                            deliveryTest.loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-900 hover:bg-gray-800'
                          }`}
                        >
                          {deliveryTest.loading ? 'Checking…' : 'Check'}
                        </button>
                      </div>

                      {deliveryTest.error && (
                        <p className="text-sm text-red-600 mt-3">{deliveryTest.error}</p>
                      )}

                      {deliveryTest.result && (
                        <div className="mt-4 bg-gray-50 rounded-xl p-4 text-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircleIcon className={`w-5 h-5 ${deliveryTest.result.serviceable ? 'text-green-600' : 'text-amber-600'}`} />
                            <span className="font-semibold text-gray-900">{deliveryTest.result.reason}</span>
                          </div>
                          <div className="text-gray-700">
                            <div><span className="font-semibold">Serviceable:</span> {String(deliveryTest.result.serviceable)}</div>
                            <div><span className="font-semibold">COD Available:</span> {String(deliveryTest.result.codAvailable)}</div>
                            <div><span className="font-semibold">Configured:</span> {String(deliveryTest.result.configured)}</div>
                            <div><span className="font-semibold">Mode:</span> {deliveryTest.result.mode}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'payments' && (
              <div className="space-y-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Payment Settings</h2>
                    <p className="text-gray-600 mt-1">
                      Set your UPI ID (this is the “Google Pay ID”) and payee name. Customers will use this to initiate UPI payments.
                    </p>
                  </div>

                  <button
                    onClick={handleSavePayments}
                    disabled={savingPayments}
                    className={`w-full rounded-xl px-6 py-3 font-semibold text-white transition-all sm:w-auto ${
                      savingPayments ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {savingPayments ? 'Saving…' : 'Save Payment Settings'}
                  </button>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">UPI / Google Pay (Intent)</h3>
                      <p className="text-sm text-gray-600">Works with Google Pay / PhonePe / Paytm via UPI intent (upi://pay)</p>
                    </div>

                    <label className="flex items-center gap-3 self-start">
                      <span className="text-sm font-semibold text-gray-700">Enable</span>
                      <input
                        type="checkbox"
                        checked={paymentConfig.upiEnabled}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, upiEnabled: e.target.checked }))}
                        className="w-5 h-5 text-indigo-600 rounded"
                      />
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">UPI ID (VPA) *</label>
                      <input
                        type="text"
                        value={paymentConfig.upiVpa}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, upiVpa: e.target.value }))}
                        placeholder="yourname@okaxis"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono"
                      />
                      <p className="text-xs text-gray-500 mt-2">This is what you call “GPay ID”. Example: <span className="font-mono">viverra@okaxis</span></p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Payee Name *</label>
                      <input
                        type="text"
                        value={paymentConfig.upiPayeeName}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, upiPayeeName: e.target.value }))}
                        placeholder="Viverra Fancy"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Merchant Code (optional)</label>
                      <input
                        type="text"
                        value={paymentConfig.upiMerchantCode}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, upiMerchantCode: e.target.value }))}
                        placeholder="1234"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Website URL (optional)</label>
                      <input
                        type="text"
                        value={paymentConfig.upiWebsiteUrl}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, upiWebsiteUrl: e.target.value }))}
                        placeholder="https://yourdomain.com"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Default Note Prefix</label>
                      <input
                        type="text"
                        value={paymentConfig.upiNotePrefix}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, upiNotePrefix: e.target.value }))}
                        placeholder="Order"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                      />
                      <p className="text-xs text-gray-500 mt-2">Example note shown in payment app: “Order #SI-2001”.</p>
                    </div>
                  </div>

                  <div className="mt-6 p-4 rounded-xl bg-white border border-gray-200">
                    <p className="text-sm text-gray-700">
                      Note: This only <span className="font-semibold">initiates</span> payment. You should not auto-mark orders as paid unless verified.
                      Recommended flow: customer pays via UPI → admin confirms payment → mark as paid.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Paytm Payment Gateway (Automatic Confirmation)</h3>
                      <p className="text-sm text-gray-600">Requires Paytm PG integration + callback URL (webhook-like). Merchant key is kept admin-only.</p>
                    </div>

                    <label className="flex items-center gap-3 self-start">
                      <span className="text-sm font-semibold text-gray-700">Enable</span>
                      <input
                        type="checkbox"
                        checked={paymentConfig.paytmEnabled}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, paytmEnabled: e.target.checked }))}
                        className="w-5 h-5 text-indigo-600 rounded"
                      />
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">MID (Merchant ID) *</label>
                      <input
                        type="text"
                        value={paymentConfig.paytmMid}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, paytmMid: e.target.value }))}
                        placeholder="Your Paytm MID"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Merchant Key (Checksum Key) *</label>
                      <input
                        type="password"
                        value={paymentConfig.paytmMerchantKey}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, paytmMerchantKey: e.target.value }))}
                        placeholder="Your Paytm Merchant Key"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono"
                      />
                      <p className="text-xs text-gray-500 mt-2">This is sensitive and only loaded via admin-only endpoint.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Environment</label>
                      <select
                        value={paymentConfig.paytmEnvironment}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, paytmEnvironment: e.target.value }))}
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                      >
                        <option value="STAGING">STAGING</option>
                        <option value="PRODUCTION">PRODUCTION</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Website Name</label>
                      <input
                        type="text"
                        value={paymentConfig.paytmWebsiteName}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, paytmWebsiteName: e.target.value }))}
                        placeholder="WEBSTAGING / DEFAULT"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono"
                      />
                      <p className="text-xs text-gray-500 mt-2">Staging usually uses <span className="font-mono">WEBSTAGING</span>.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Callback URL (optional)</label>
                      <input
                        type="text"
                        value={paymentConfig.paytmCallbackUrl}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, paytmCallbackUrl: e.target.value }))}
                        placeholder="https://yourdomain.com/api/paytm/callback"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                      />
                      <p className="text-xs text-gray-500 mt-2">If empty, backend auto-builds it from the current request host.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Channel ID</label>
                      <input
                        type="text"
                        value={paymentConfig.paytmChannelId}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, paytmChannelId: e.target.value }))}
                        placeholder="WEB"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono"
                      />
                    </div>
                  </div>

                  <div className="mt-6 p-4 rounded-xl bg-white border border-gray-200">
                    <p className="text-sm text-gray-700">
                      This enables <span className="font-semibold">automatic</span> payment confirmation: Paytm calls back to your backend, and the backend verifies the signature before marking the order as <span className="font-semibold">PAID</span>.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Payment Links (UPI via Razorpay/Cashfree)</h3>
                      <p className="text-sm text-gray-600">Recommended for automatic UPI confirmation via verified webhooks.</p>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      <span className="text-sm font-semibold text-gray-700">Default</span>
                      <select
                        value={paymentConfig.paymentLinksDefaultProvider}
                        onChange={(e) => setPaymentConfig(prev => ({ ...prev, paymentLinksDefaultProvider: e.target.value }))}
                        className="w-full rounded-xl border-2 border-gray-300 bg-white px-4 py-3 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:w-auto"
                      >
                        <option value="auto">Auto (first enabled)</option>
                        <option value="razorpay">Razorpay</option>
                        <option value="cashfree">Cashfree</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-2xl p-5 border border-gray-200">
                      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <h4 className="text-base font-bold text-gray-900">Razorpay</h4>
                          <p className="text-xs text-gray-600">Creates a Payment Link and confirms via webhook signature.</p>
                        </div>
                        <label className="flex items-center gap-3 self-start">
                          <span className="text-sm font-semibold text-gray-700">Enable</span>
                          <input
                            type="checkbox"
                            checked={paymentConfig.razorpayEnabled}
                            onChange={(e) => setPaymentConfig(prev => ({ ...prev, razorpayEnabled: e.target.checked }))}
                            className="w-5 h-5 text-indigo-600 rounded"
                          />
                        </label>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Key ID *</label>
                          <input
                            type="text"
                            value={paymentConfig.razorpayKeyId}
                            onChange={(e) => setPaymentConfig(prev => ({ ...prev, razorpayKeyId: e.target.value }))}
                            placeholder="rzp_live_..."
                            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Key Secret *</label>
                          <input
                            type="password"
                            value={paymentConfig.razorpayKeySecret}
                            onChange={(e) => setPaymentConfig(prev => ({ ...prev, razorpayKeySecret: e.target.value }))}
                            placeholder="(secret)"
                            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Webhook Secret *</label>
                          <input
                            type="password"
                            value={paymentConfig.razorpayWebhookSecret}
                            onChange={(e) => setPaymentConfig(prev => ({ ...prev, razorpayWebhookSecret: e.target.value }))}
                            placeholder="Set in Razorpay Dashboard → Webhooks"
                            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Callback URL (optional)</label>
                          <input
                            type="text"
                            value={paymentConfig.razorpayCallbackUrl}
                            onChange={(e) => setPaymentConfig(prev => ({ ...prev, razorpayCallbackUrl: e.target.value }))}
                            placeholder="https://viverra.in/checkout/return"
                            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                          />
                        </div>
                      </div>

                      <div className="mt-4 p-4 rounded-xl bg-gray-50 border border-gray-200">
                        <p className="text-xs text-gray-700">
                          Webhook URL: <span className="font-mono">/api/payments/webhook/razorpay</span>
                        </p>
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl p-5 border border-gray-200">
                      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <h4 className="text-base font-bold text-gray-900">Cashfree</h4>
                          <p className="text-xs text-gray-600">Creates a Payment Link and confirms via webhook signature.</p>
                        </div>
                        <label className="flex items-center gap-3 self-start">
                          <span className="text-sm font-semibold text-gray-700">Enable</span>
                          <input
                            type="checkbox"
                            checked={paymentConfig.cashfreeEnabled}
                            onChange={(e) => setPaymentConfig(prev => ({ ...prev, cashfreeEnabled: e.target.checked }))}
                            className="w-5 h-5 text-indigo-600 rounded"
                          />
                        </label>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Environment</label>
                          <select
                            value={paymentConfig.cashfreeEnvironment}
                            onChange={(e) => setPaymentConfig(prev => ({ ...prev, cashfreeEnvironment: e.target.value }))}
                            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                          >
                            <option value="SANDBOX">SANDBOX</option>
                            <option value="PROD">PROD</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Client ID *</label>
                          <input
                            type="text"
                            value={paymentConfig.cashfreeClientId}
                            onChange={(e) => setPaymentConfig(prev => ({ ...prev, cashfreeClientId: e.target.value }))}
                            placeholder="(client id)"
                            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Client Secret *</label>
                          <input
                            type="password"
                            value={paymentConfig.cashfreeClientSecret}
                            onChange={(e) => setPaymentConfig(prev => ({ ...prev, cashfreeClientSecret: e.target.value }))}
                            placeholder="(secret)"
                            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Webhook Secret *</label>
                          <input
                            type="password"
                            value={paymentConfig.cashfreeWebhookSecret}
                            onChange={(e) => setPaymentConfig(prev => ({ ...prev, cashfreeWebhookSecret: e.target.value }))}
                            placeholder="Set in Cashfree Dashboard → Webhooks"
                            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">API Version</label>
                          <input
                            type="text"
                            value={paymentConfig.cashfreeApiVersion}
                            onChange={(e) => setPaymentConfig(prev => ({ ...prev, cashfreeApiVersion: e.target.value }))}
                            placeholder="2023-08-01"
                            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Return URL (optional)</label>
                          <input
                            type="text"
                            value={paymentConfig.cashfreeReturnUrl}
                            onChange={(e) => setPaymentConfig(prev => ({ ...prev, cashfreeReturnUrl: e.target.value }))}
                            placeholder="https://viverra.in/checkout/return"
                            className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                          />
                        </div>
                      </div>

                      <div className="mt-4 p-4 rounded-xl bg-gray-50 border border-gray-200">
                        <p className="text-xs text-gray-700">
                          Webhook URL: <span className="font-mono">/api/payments/webhook/cashfree</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 rounded-xl bg-white border border-gray-200">
                    <p className="text-sm text-gray-700">
                      Golden rule: orders are marked <span className="font-semibold">PAID</span> only from a <span className="font-semibold">verified webhook</span>, not from the return/success page.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Notifications</h2>
                    <p className="text-gray-600 mt-1">
                      Configure customer password reset email sender details and message content from admin.
                    </p>
                  </div>

                  <button
                    onClick={handleSaveNotifications}
                    disabled={savingNotifications}
                    className={`w-full rounded-xl px-6 py-3 font-semibold text-white transition-all sm:w-auto ${
                      savingNotifications ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {savingNotifications ? 'Saving…' : 'Save Notification Settings'}
                  </button>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Password Reset Email</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">From Name</label>
                      <input
                        type="text"
                        value={notificationConfig.fromName}
                        onChange={(e) => setNotificationConfig((prev) => ({ ...prev, fromName: e.target.value }))}
                        placeholder="Viverra Fancy"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">From Email (optional override)</label>
                      <input
                        type="email"
                        value={notificationConfig.fromEmail}
                        onChange={(e) => setNotificationConfig((prev) => ({ ...prev, fromEmail: e.target.value }))}
                        placeholder="no-reply@yourdomain.com"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                      />
                      <p className="text-xs text-gray-500 mt-2">If empty, backend uses environment mail sender.</p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email Subject</label>
                      <input
                        type="text"
                        value={notificationConfig.subject}
                        onChange={(e) => setNotificationConfig((prev) => ({ ...prev, subject: e.target.value }))}
                        placeholder="Reset your password"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Intro Message</label>
                      <textarea
                        rows={3}
                        value={notificationConfig.introText}
                        onChange={(e) => setNotificationConfig((prev) => ({ ...prev, introText: e.target.value }))}
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                        placeholder="We received a request to reset your password."
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Footer Message</label>
                      <textarea
                        rows={3}
                        value={notificationConfig.footerText}
                        onChange={(e) => setNotificationConfig((prev) => ({ ...prev, footerText: e.target.value }))}
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                        placeholder="If you did not request this, you can ignore this email."
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Reset Link Base URL (optional)</label>
                      <input
                        type="url"
                        value={notificationConfig.resetLinkBaseUrl}
                        onChange={(e) => setNotificationConfig((prev) => ({ ...prev, resetLinkBaseUrl: e.target.value }))}
                        placeholder="https://viverra.in"
                        className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                      />
                      <p className="text-xs text-gray-500 mt-2">Used to build `/reset-password?token=...` links.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h3 className="text-lg font-bold text-gray-900">SMS Template (for future OTP/SMS provider)</h3>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <span>Enable SMS metadata</span>
                      <input
                        type="checkbox"
                        checked={notificationConfig.smsEnabled}
                        onChange={(e) => setNotificationConfig((prev) => ({ ...prev, smsEnabled: e.target.checked }))}
                        className="w-5 h-5 text-indigo-600 rounded"
                      />
                    </label>
                  </div>

                  <textarea
                    rows={4}
                    value={notificationConfig.smsTemplate}
                    onChange={(e) => setNotificationConfig((prev) => ({ ...prev, smsTemplate: e.target.value }))}
                    className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all border-gray-300 bg-white"
                    placeholder="Reset your account password using this link: {{reset_link}}"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Placeholder supported: <span className="font-mono">{'{{reset_link}}'}</span>. This stores message content in admin settings; SMS provider hookup can use it later.
                  </p>
                </div>
              </div>
            )}

            {/* Preview Tab */}
            {activeTab === 'preview' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Live Preview</h2>
                
                <div className="border-4 border-gray-200 rounded-xl bg-white p-4 sm:p-6 lg:p-8">
                  {/* Header Preview */}
                  <div 
                    className="p-4 rounded-lg mb-6"
                    style={{ backgroundColor: designConfig.header_bg }}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                        style={{ backgroundColor: designConfig.primary_color }}
                      >
                        {designConfig.logo_text}
                      </div>
                      <span 
                        className="text-xl font-bold"
                        style={{ color: designConfig.text_color }}
                      >
                        {designConfig.site_name}
                      </span>
                    </div>
                  </div>

                  {/* Hero Preview */}
                  <div 
                    className="mb-6 rounded-lg p-6 text-center sm:p-12"
                    style={{ 
                      background: `linear-gradient(135deg, ${designConfig.primary_color}, ${designConfig.secondary_color})`,
                      color: 'white'
                    }}
                  >
                    <h1 className="mb-4 text-3xl font-bold sm:text-4xl">{designConfig.hero_title}</h1>
                    <p className="mb-6 text-base opacity-90 sm:text-xl">{designConfig.hero_subtitle}</p>
                    <button 
                      className="rounded-full px-6 py-3 font-semibold text-white sm:px-8"
                      style={{ backgroundColor: designConfig.accent_color }}
                    >
                      {designConfig.hero_button_text}
                    </button>
                  </div>

                  {/* Features Preview */}
                  <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {[1, 2, 3].map((num) => (
                      <div key={num} className="text-center p-4 border-2 border-gray-200 rounded-lg">
                        <div 
                          className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center"
                          style={{ backgroundColor: designConfig.primary_color }}
                        >
                          <span className="text-white text-2xl">⭐</span>
                        </div>
                        <h3 className="font-bold mb-2" style={{ color: designConfig.text_color }}>
                          {designConfig[`feature_${num}_title`]}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {designConfig[`feature_${num}_description`]}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Footer Preview */}
                  <div 
                    className="p-6 rounded-lg"
                    style={{ 
                      backgroundColor: designConfig.footer_bg,
                      color: designConfig.footer_text
                    }}
                  >
                    <p className="mb-4">{designConfig.footer_about}</p>
                    <p className="text-sm opacity-75">{designConfig.footer_copyright}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="mt-8 flex flex-col-reverse gap-3 border-t border-gray-200 pt-6 sm:flex-row sm:justify-end sm:gap-4">
              <button
                onClick={() => window.location.reload()}
                className="w-full rounded-xl border-2 border-gray-300 px-6 py-3 font-semibold transition-colors hover:bg-gray-50 sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={getFooterSaveHandler()}
                disabled={isFooterSaving}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 px-8 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:from-indigo-600 hover:to-purple-600 disabled:opacity-50 sm:w-auto sm:hover:scale-105"
              >
                {isFooterSaving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-5 h-5" />
                    {getFooterSaveLabel()}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </PermissionGuard>
  );
};

export default AdminSettings;
